<template>
  <main-layout>
    <template #header>
      <main-header :title="'소모품 교체 현황' | translate">
        <template #append-title>
          <menu-beta-badge />
        </template>
      </main-header>
    </template>
    <template #toolbar>
      <div class="supply-toolbar query-box">
        <div class="query-box-head">
          <div class="query-box-title">{{ '조회 조건' | translate }}</div>
          <div class="query-box-notice" role="note">
            <span class="notice-lead">{{ noticeLead }}</span>
            <button type="button" class="notice-more" @click="noticeDetail = !noticeDetail">
              {{ (noticeDetail ? '접기' : '자세히 보기') | translate }}
              <span v-if="!noticeDetail" aria-hidden="true">&gt;</span>
            </button>
          </div>
        </div>
        <p v-if="noticeDetail" class="data-quality-detail">{{ noticeBody }}</p>
        <select v-model="search.rentalcompanyId" class="supply-control" @change="reload">
          <option :value="null">{{ '전체 임대업체' | translate }}</option>
          <option
            v-for="item in rentalcompanyOptions"
            :key="item.rentalcompanyId"
            :value="item.rentalcompanyId"
          >
            {{ item.rentalcompanyName }}
          </option>
        </select>
        <template v-if="showPeriodToolbar">
          <div class="preset-group">
            <button
              v-for="p in periodPresets"
              :key="p.key"
              type="button"
              class="preset-btn"
              :class="{ on: periodPreset === p.key }"
              @click="applyPreset(p.key)"
            >
              {{ p.label | translate }}
            </button>
          </div>
          <input
            v-model="search.fromDate"
            type="date"
            class="supply-control"
            @change="onCustomDate"
          />
          <span>~</span>
          <input
            v-model="search.toDate"
            type="date"
            class="supply-control"
            @change="onCustomDate"
          />
        </template>
        <template v-else-if="showHorizonToolbar">
          <span class="toolbar-label">{{ '계획 기간' | translate }}</span>
          <div class="preset-group">
            <button
              v-for="h in horizonPresets"
              :key="'h-' + h"
              type="button"
              class="preset-btn"
              :class="{ on: search.horizonDays === h }"
              @click="applyHorizon(h)"
            >
              {{ h }}{{ '일' | translate }}
            </button>
          </div>
          <span class="toolbar-label">{{ '주기 산출' | translate }}</span>
          <select
            v-model.number="search.cycleLookbackDays"
            class="supply-control"
            @change="reload"
          >
            <option :value="90">{{ '최근 90일 이력' | translate }}</option>
            <option :value="180">{{ '최근 180일 이력' | translate }}</option>
            <option :value="365">{{ '최근 365일 이력' | translate }}</option>
          </select>
        </template>
        <select v-if="activeTab !== 'dashboard'" v-model="search.supplyCategory" class="supply-control">
          <option value="">{{ '전체 소모품' | translate }}</option>
          <option value="TONER">TONER</option>
          <option value="DRUM">DRUM</option>
          <option value="WASTE">WASTE</option>
          <option value="OTHER">OTHER</option>
        </select>
        <input
          v-model="search.partNumber"
          type="text"
          class="supply-control"
          :placeholder="'품번' | translate"
        />
        <button class="supply-btn query-submit" @click="reload">{{ '조회' | translate }}</button>
        <button
          v-if="activeTab === 'usage'"
          class="supply-btn outline"
          @click="exportUsageCsv"
        >
          {{ 'CSV' | translate }}
        </button>
      </div>
    </template>
    <template #body>
      <supply-replace-history-drawer
        v-model="historyDrawerOpen"
        :title="historyDrawerTitle"
        :mode="historyDrawerMode"
        :query="historyQuery"
        :show-excel="historyShowExcel"
        @open-account="openAccountDetail"
        @open-printer="openPrinterDetail"
      />
      <supply-common-part-drawer
        v-model="commonPartOpen"
        :context="commonPartContext"
        @saved="onCommonPartSaved"
      />
      <div class="supply-body">
        <div class="content-tabs">
          <button
            v-for="tab in contentTabs"
            :key="tab.value"
            type="button"
            :class="{ active: activeTab === tab.value }"
            @click="changeContentTab(tab.value)"
          >
            {{ tab.label | translate }}
          </button>
        </div>
        <p v-if="activeTab === 'sales'" class="hint">
          {{
            '제조사·모델·품번·설명으로 찾고 소모품별 / 프린터 모델별 / 임대업체별로 전환합니다. 횡단 목록에는 임대업체명·모델·품번·집계만 있습니다.'
              | translate
          }}
        </p>

        <div v-if="activeTab === 'plan'" class="supply-tabs status-view-tabs">
          <button
            v-for="tab in groupTabs"
            :key="tab.value"
            type="button"
            :class="{ active: search.groupBy === tab.value }"
            @click="changeGroup(tab.value)"
          >
            {{ tab.label | translate }}
          </button>
        </div>
        <!-- 대시보드 -->
        <template v-if="activeTab === 'dashboard'">
          <div class="dash-row">
            <div class="part-card part-card--full">
              <div class="part-card-head">
                <h3>{{ '요약' | translate }}</h3>
              </div>
              <div class="item-card-grid kpi-grid">
                <div
                  class="item-card clickable"
                  data-supply-drawer-keep
                  role="button"
                  tabindex="0"
                  @click="openDashboardDrawer('history')"
                  @keydown.enter="openDashboardDrawer('history')"
                >
                  <div class="label">{{ '기간 총 교체수' | translate }}</div>
                  <div class="value">{{ overview.totalReplaceCount }}</div>
                </div>
                <div
                  class="item-card clickable"
                  data-supply-drawer-keep
                  role="button"
                  tabindex="0"
                  @click="openDashboardDrawer('lowEmpty')"
                  @keydown.enter="openDashboardDrawer('lowEmpty')"
                >
                  <div class="label">{{ 'LOW/EMPTY 슬롯' | translate }}</div>
                  <div class="value">
                    {{
                      overview.lowEmptyItemCount != null
                        ? overview.lowEmptyItemCount
                        : overview.lowEmptyPrinterCount
                    }}
                  </div>
                </div>
                <div
                  class="item-card clickable"
                  data-supply-drawer-keep
                  role="button"
                  tabindex="0"
                  @click="openDashboardDrawer('forecast')"
                  @keydown.enter="openDashboardDrawer('forecast')"
                >
                  <div class="label">{{ '예측 가능 품목' | translate }}</div>
                  <div class="value">{{ overview.forecastItemCount }}</div>
                </div>
                <div
                  class="item-card clickable"
                  data-supply-drawer-keep
                  role="button"
                  tabindex="0"
                  @click="openDashboardDrawer('error')"
                  @keydown.enter="openDashboardDrawer('error')"
                >
                  <div class="label">{{ '에러 슬롯' | translate }}</div>
                  <div class="value">{{ overview.errorCount || 0 }}</div>
                </div>
              </div>
            </div>

            <div class="part-card part-card--full">
              <div class="part-card-head">
                <h3>{{ '구분별 교체수' | translate }}</h3>
                <div class="part-card-actions">
                  <span class="breakdown-total">
                    {{ '합계' | translate }} {{ categoryReplaceTotal }}
                  </span>
                  <button
                    type="button"
                    class="supply-btn outline"
                    :class="{ on: categorySort.key === 'itemKey' }"
                    @click="sortBreakdown('category', 'itemKey')"
                  >
                    {{ '항목' | translate }}{{ breakdownSortMark('category', 'itemKey') }}
                  </button>
                  <button
                    type="button"
                    class="supply-btn outline"
                    :class="{ on: categorySort.key === 'replaceCount' }"
                    @click="sortBreakdown('category', 'replaceCount')"
                  >
                    {{ '교체수' | translate }}{{ breakdownSortMark('category', 'replaceCount') }}
                  </button>
                </div>
              </div>
              <div class="item-card-grid">
                <div
                  v-for="item in filteredCategoryBreakdown"
                  :key="'dash-cat-' + item.itemKey"
                  class="item-card clickable"
                  data-supply-drawer-keep
                  :class="'supply-card--' + (item.itemKey || 'OTHER').toLowerCase()"
                  role="button"
                  tabindex="0"
                  @click="openBreakdownDrawer('category', item)"
                  @keydown.enter="openBreakdownDrawer('category', item)"
                >
                  <div class="label">{{ formatCategory(item.itemKey) }}</div>
                  <div class="value">{{ item.replaceCount || 0 }}</div>
                </div>
                <div v-if="!filteredCategoryBreakdown.length" class="empty">
                  {{ '데이터가 없습니다.' | translate }}
                </div>
              </div>
            </div>

            <div class="part-card part-card--full">
              <div class="part-card-head">
                <h3>{{ '제조사별 교체수' | translate }}</h3>
                <div class="part-card-actions">
                  <span class="breakdown-total">
                    {{ '합계' | translate }} {{ manufacturerReplaceTotal }}
                  </span>
                  <button
                    type="button"
                    class="supply-btn outline"
                    :class="{ on: manufacturerSort.key === 'itemKey' }"
                    @click="sortBreakdown('manufacturer', 'itemKey')"
                  >
                    {{ '항목' | translate }}{{ breakdownSortMark('manufacturer', 'itemKey') }}
                  </button>
                  <button
                    type="button"
                    class="supply-btn outline"
                    :class="{ on: manufacturerSort.key === 'replaceCount' }"
                    @click="sortBreakdown('manufacturer', 'replaceCount')"
                  >
                    {{ '교체수' | translate }}{{ breakdownSortMark('manufacturer', 'replaceCount') }}
                  </button>
                </div>
              </div>
              <div class="item-card-grid">
                <div
                  v-for="item in filteredManufacturerBreakdown"
                  :key="'dash-mfr-' + item.itemKey"
                  class="item-card clickable"
                  data-supply-drawer-keep
                  role="button"
                  tabindex="0"
                  @click="openBreakdownDrawer('manufacturer', item)"
                  @keydown.enter="openBreakdownDrawer('manufacturer', item)"
                >
                  <div class="label">{{ item.itemKey || '-' }}</div>
                  <div class="value">{{ item.replaceCount || 0 }}</div>
                </div>
                <div v-if="!filteredManufacturerBreakdown.length" class="empty">
                  {{ '데이터가 없습니다.' | translate }}
                </div>
              </div>
            </div>

            <div class="part-card part-card--full">
              <div class="part-card-head">
                <h3>{{ '교체 추이' | translate }}</h3>
                <select v-model="search.trendBucket" class="supply-control" @change="loadTrend">
                  <option value="DAY">{{ '일별' | translate }}</option>
                  <option value="WEEK">{{ '주별' | translate }}</option>
                  <option value="MONTH">{{ '월별' | translate }}</option>
                </select>
              </div>
              <div class="trend-bars">
                <div
                  v-for="(label, idx) in trendLabels"
                  :key="'trend-' + idx + '-' + label"
                  class="trend-bar-item"
                  :title="label + ': ' + trendValues[idx]"
                >
                  <div class="bar" :style="{ height: barHeight(trendValues[idx]) + 'px' }"></div>
                  <div class="bar-label">{{ label }}</div>
                  <div class="bar-value">{{ trendValues[idx] }}</div>
                </div>
                <div v-if="!trendLabels.length" class="empty">
                  {{ '데이터가 없습니다.' | translate }}
                </div>
              </div>
            </div>

            <div class="part-card part-card--full">
              <div class="part-card-head">
                <h3>{{ '거래처별 교체 현황' | translate }}</h3>
                <span class="supply-account-basis">
                  {{ '조회 기간 전체 기준 · 교체수 많은 순' | translate }}
                </span>
              </div>
              <div v-if="!accountReplaceRows.length" class="empty">
                {{ '표시할 거래처별 교체 현황 데이터가 없습니다.' | translate }}
              </div>
              <div v-else class="supply-account-table-wrap">
                <table class="supply-account-table">
                  <thead>
                    <tr>
                      <th>{{ '임대업체' | translate }}</th>
                      <th>{{ '거래처' | translate }}</th>
                      <th class="num">{{ '교체수' | translate }}</th>
                      <th class="num">{{ '프린터 수' | translate }}</th>
                      <th class="num">{{ '월간 예측' | translate }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in accountReplaceRows"
                      :key="'acct-replace-' + (row.accountId || row.groupKey)"
                    >
                      <td>{{ row.rentalcompanyName || '-' }}</td>
                      <td>
                        <button
                          type="button"
                          class="supply-account-link"
                          @click="openAccountReplace(row)"
                        >
                          {{ row.accountName || row.groupLabel || '-' }}
                        </button>
                      </td>
                      <td class="num">{{ row.replaceCount || 0 }}</td>
                      <td class="num">{{ row.printerCount || 0 }}</td>
                      <td class="num">{{ formatForecast(row.monthlyForecast) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="activeTab === 'sales'">
          <supply-sales-intel-panel
            :rows="salesRows"
            :keyword.sync="salesKeyword"
            :search-type.sync="salesSearchType"
            :group-by="search.groupBy"
            :suggestions="salesSuggestions"
            :kpis="salesKpis"
            :drill="salesDrill"
            :page="search.page"
            :total-pages="salesTotalPages"
            :has-filter="hasSalesFilter"
            @search="onSalesSearch"
            @suggest="onSalesSuggest"
            @pick="onSalesPick"
            @clear="clearSalesSearch"
            @export="exportSalesCsv"
            @change-group="onSalesChangeGroup"
            @row-click="onSalesRowClick"
            @sku-insight="openSalesSkuInsight"
            @clear-drill="clearSalesDrill"
            @page-change="onSalesPageChange"
            @kpi="onSalesKpi"
          />
        </template>

        <template v-else-if="activeTab === 'status'">
          <div class="supply-tabs status-view-tabs">
            <button
              v-for="tab in groupTabs"
              :key="'status-' + tab.value"
              type="button"
              :class="{ active: search.groupBy === tab.value }"
              @click="changeGroup(tab.value)"
            >
              {{ tab.label | translate }}
            </button>
          </div>
          <supply-sku-insight-panel
            :rows="skuInsightRows"
            :category-cards="statusCategoryCardItems"
            :selected-category="search.supplyCategory"
            @category="filterStatusCategory"
            :group-by="search.groupBy"
            :keyword.sync="skuKeyword"
            :include-unmapped.sync="statusIncludeUnmapped"
            :total-count="skuTotalCount"
            :error-count="skuInsightTotals.errorCount"
            :low-empty-count="skuInsightTotals.lowEmptyCount"
            :urgent-count="skuInsightTotals.urgentCount"
            :page="skuPage"
            :rows-per-page="skuRows"
            :order-by="skuOrderBy"
            :order-dir="skuOrderDir"
            @search="onSkuSearch"
            @sort="onSkuSort"
            @page-change="onSkuPageChange"
            @detail="onSkuDetail"
            @kpi="onSkuKpi"
            @rental-rank="onSkuRentalRank"
            @map="openCommonPartMap"
          />
        </template>

        <!-- 교체수 / 월간 예측 -->
        <template v-else-if="activeTab === 'usage'">
          <div class="supply-panel">
            <div class="panel-head">
              <h3>{{ '교체수 / 월간 예측' | translate }}</h3>
              <div class="list-tools">
                <select v-model="search.colorCode" class="supply-control" @change="reloadListOnly">
                  <option value="">{{ '전체 색상' | translate }}</option>
                  <option
                    v-for="color in colorOptions"
                    :key="color"
                    :value="color"
                  >
                    {{ color }}
                  </option>
                </select>
                <input
                  v-model="search.keyword"
                  type="text"
                  class="supply-control search-wide"
                  :placeholder="usageSearchPlaceholder"
                  @keydown.enter.prevent="reloadListOnly"
                />
                <button class="supply-btn outline" @click="reloadListOnly">
                  {{ '검색' | translate }}
                </button>
                <button
                  v-if="hasUsageListFilter"
                  type="button"
                  class="supply-btn outline"
                  @click="clearUsageListFilter"
                >
                  {{ '필터 초기화' | translate }}
                </button>
              </div>
            </div>
            <table class="supply-table">
              <thead>
                <tr>
                  <th
                    v-if="!isSupplyGroup"
                    class="sortable"
                    @click="sortBy('groupLabel')"
                  >
                    {{ usageGroupColumnLabel }}{{ sortMark('groupLabel') }}
                  </th>
                  <th
                    v-if="isSupplyGroup"
                    class="sortable"
                    @click="sortBy('partNumber')"
                  >
                    {{ '품번' | translate }}{{ sortMark('partNumber') }}
                  </th>
                  <th v-if="isSupplyGroup">{{ '소모품 기본정보' | translate }}</th>
                  <th
                    v-if="isSupplyGroup"
                    class="sortable"
                    @click="sortBy('supplyCategory')"
                  >
                    {{ '구분' | translate }}{{ sortMark('supplyCategory') }}
                  </th>
                  <th
                    v-if="isSupplyGroup"
                    class="sortable"
                    @click="sortBy('manufacturerName')"
                  >
                    {{ '제조사' | translate }}{{ sortMark('manufacturerName') }}
                  </th>
                  <th v-if="isSupplyGroup">{{ '유형' | translate }}</th>
                  <th class="sortable" @click="sortBy('replaceCount')">
                    {{ '교체수' | translate }}{{ sortMark('replaceCount') }}
                  </th>
                  <th class="sortable" @click="sortBy('printerCount')">
                    {{ '프린터수' | translate }}{{ sortMark('printerCount') }}
                  </th>
                  <th class="sortable" @click="sortBy('rentalCompanyCount')">
                    {{ '임대업체수' | translate }}{{ sortMark('rentalCompanyCount') }}
                  </th>
                  <th class="sortable" @click="sortBy('avgCycleDays')">
                    {{ '평균주기(일)' | translate }}{{ sortMark('avgCycleDays') }}
                  </th>
                  <th class="sortable" @click="sortBy('monthlyForecast')">
                    {{ '월간예측' | translate }}{{ sortMark('monthlyForecast') }}
                  </th>
                  <th class="sortable" @click="sortBy('pagesPerCycleA4')">
                    {{ '출력효율(장/회)' | translate }}{{ sortMark('pagesPerCycleA4') }}
                  </th>
                  <th class="sortable" @click="sortBy('lastReplacedDatetime')">
                    {{ '최근교체' | translate }}{{ sortMark('lastReplacedDatetime') }}
                  </th>
                  <th v-if="isSupplyGroup">{{ '공용 품번' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!usageList.length">
                  <td :colspan="usageColspan" class="empty">
                    {{ '데이터가 없습니다.' | translate }}
                  </td>
                </tr>
                <tr
                  v-for="(row, idx) in usageList"
                  :key="'usage-' + idx"
                  class="clickable-row"
                  data-supply-drawer-keep
                  @click="openRowHistory(row)"
                >
                  <td v-if="!isSupplyGroup">{{ formatGroupLabel(row) }}</td>
                  <td v-if="isSupplyGroup">{{ row.partNumber || '-' }}</td>
                  <td
                    v-if="isSupplyGroup"
                    class="info-cell"
                    :title="formatSupplyInfo(row)"
                  >
                    {{ formatSupplyInfo(row) }}
                  </td>
                  <td v-if="isSupplyGroup">
                    {{ formatCategory(row.supplyCategory) }} {{ formatColorCode(row.colorCode) }}
                  </td>
                  <td v-if="isSupplyGroup">{{ formatManufacturer(row) }}</td>
                  <td v-if="isSupplyGroup">{{ formatCompatibilityType(row.compatibilityType) }}</td>
                  <td>{{ row.replaceCount || 0 }}</td>
                  <td>{{ row.printerCount || 0 }}</td>
                  <td>{{ row.rentalCompanyCount || 0 }}</td>
                  <td>
                    <span
                      class="metric-with-compare"
                      :title="benchmarkTooltip(row, 'cycle')"
                    >
                      {{ formatNullable(row.avgCycleDays) }}
                      <em
                        v-if="benchmarkBadge(row.cycleCompare)"
                        class="compare-badge"
                        :class="'compare-' + String(row.cycleCompare || '').toLowerCase()"
                      >{{ benchmarkBadge(row.cycleCompare) }}</em>
                    </span>
                  </td>
                  <td>
                    <span
                      class="metric-with-compare"
                      :title="benchmarkTooltip(row, 'forecast')"
                    >
                      {{ formatNullable(row.monthlyForecast) }}
                      <em
                        v-if="benchmarkBadge(row.forecastCompare)"
                        class="compare-badge"
                        :class="'compare-' + String(row.forecastCompare || '').toLowerCase()"
                      >{{ benchmarkBadge(row.forecastCompare) }}</em>
                    </span>
                  </td>
                  <td>
                    <span
                      class="metric-with-compare"
                      :title="benchmarkTooltip(row, 'efficiency')"
                    >
                      {{ formatNullable(row.pagesPerCycleA4) }}
                      <em
                        v-if="benchmarkBadge(row.efficiencyCompare)"
                        class="compare-badge"
                        :class="'compare-' + String(row.efficiencyCompare || '').toLowerCase()"
                      >{{ benchmarkBadge(row.efficiencyCompare) }}</em>
                    </span>
                  </td>
                  <td class="nowrap">{{ row.lastReplacedDatetime || '-' }}</td>
                  <td v-if="isSupplyGroup">
                    <button
                      type="button"
                      class="map-link-btn"
                      data-supply-drawer-keep
                      @click.stop="openCommonPartMap(row)"
                    >
                      {{ '매핑' | translate }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="pager">
              <button class="supply-btn outline" :disabled="search.page <= 1" @click="prevPage">
                {{ '이전' | translate }}
              </button>
              <span>{{ search.page }} / {{ totalPages }}</span>
              <button
                class="supply-btn outline"
                :disabled="search.page >= totalPages"
                @click="nextPage"
              >
                {{ '다음' | translate }}
              </button>
            </div>
          </div>
        </template>

        <template v-else-if="activeTab === 'efficiency'">
          <supply-efficiency-panel
            :base-payload="payload()"
            :overview-path="efficiencyOverviewPath"
            :ranking-path="efficiencyRankingPath"
            :cycles-path="efficiencyCyclesPath"
            :include-rental="true"
          />
        </template>

        <!-- 소모품 교체 계획 -->
        <template v-else-if="activeTab === 'plan'">
          <div class="plan-summary">
            <div class="plan-summary-line">
              <button
                type="button"
                class="plan-metric"
                data-supply-drawer-keep
                :title="'우측 상세창에서 긴급 교체 대상 목록을 엽니다.' | translate"
                @click="openPlanDrawerUrgent"
              >
                <span>{{ '긴급 교체 필요' | translate }}</span>
                <strong>{{ planOverview.urgentCount | currency }}</strong>
              </button>
              <span class="plan-metric">
                <span>{{ '30일 예상 수량' | translate }}</span>
                <strong>{{ planOverview.qty30 | currency }}</strong>
              </span>
              <span class="plan-metric">
                <span>{{ '90일 예상 수량' | translate }}</span>
                <strong>{{ planOverview.qty90 | currency }}</strong>
              </span>
              <span class="plan-metric">
                <span>{{ '180일 예상 수량' | translate }}</span>
                <strong>{{ planOverview.qty180 | currency }}</strong>
              </span>
              <span class="plan-metric">
                <span>{{ planTargetLabel }}</span>
                <strong>{{ planOverview.skuCount | currency }}</strong>
              </span>
              <span class="plan-metric">
                <span>{{ '데이터 부족' | translate }}</span>
                <strong>{{ planOverview.insufficientCount | currency }}</strong>
              </span>
              <button
                type="button"
                class="toggle-btn"
                :class="{ on: search.urgentOnly }"
                @click="togglePlanUrgentOnly"
              >
                {{ '긴급만' | translate }}
                <span class="toggle-state">{{ search.urgentOnly ? 'ON' : 'OFF' }}</span>
              </button>
            </div>
            <div class="plan-summary-line">
              <span class="plan-kicker">{{ '구분별' | translate }}</span>
              <button
                v-for="item in planOverview.byCategory"
                :key="'plan-cat-' + item.itemKey"
                type="button"
                class="plan-metric"
                :class="{ on: search.supplyCategory === item.itemKey }"
                :title="planBreakdownTip(item)"
                @click="filterPlanCategory(item.itemKey)"
              >
                <span>{{ formatCategory(item.itemKey) }}</span>
                <strong>{{ item.qtyHorizon || 0 | currency }}</strong>
              </button>
              <span v-if="!planOverview.byCategory.length" class="plan-empty">
                {{ '데이터가 없습니다.' | translate }}
              </span>
              <span class="plan-rule" aria-hidden="true"></span>
              <span class="plan-kicker">{{ '제조사별' | translate }}</span>
              <button
                v-for="item in planOverview.byManufacturer"
                :key="'plan-mfr-' + item.itemKey"
                type="button"
                class="plan-metric"
                :class="{ on: search.manufacturerName === item.itemKey }"
                :title="planBreakdownTip(item)"
                @click="filterPlanManufacturer(item.itemKey)"
              >
                <span>{{ formatManufacturer({ manufacturerName: item.itemKey }) }}</span>
                <strong>{{ item.qtyHorizon || 0 | currency }}</strong>
              </button>
              <span v-if="!planOverview.byManufacturer.length" class="plan-empty">
                {{ '데이터가 없습니다.' | translate }}
              </span>
            </div>
          </div>
          <div class="supply-panel">
            <div class="panel-head plan-list-head">
              <div class="list-tools plan-list-tools">
                <span class="plan-search-label">{{ '현재 목록 검색' | translate }}</span>
                <select v-model="search.colorCode" class="supply-control" @change="reloadPlanOnly">
                  <option value="">{{ '전체 색상' | translate }}</option>
                  <option v-for="color in colorOptions" :key="'plan-' + color" :value="color">
                    {{ color }}
                  </option>
                </select>
                <input
                  v-model="search.keyword"
                  type="text"
                  class="supply-control search-wide"
                  :placeholder="planSearchPlaceholder"
                  @keydown.enter.prevent="reloadPlanOnly"
                />
                <button type="button" class="supply-btn query-submit" @click="reloadPlanOnly">
                  {{ '검색' | translate }}
                </button>
              </div>
            </div>
            <div class="plan-table-wrap">
            <table class="supply-table plan-style-table">
              <thead>
                <tr class="plan-group-row">
                  <th :colspan="planBasicColspan">{{ '기본정보' | translate }}</th>
                  <th colspan="3">{{ '현재 상태' | translate }}</th>
                  <th colspan="5">{{ '교체 계획' | translate }}</th>
                  <th v-if="isSupplyGroup">{{ '관리' | translate }}</th>
                </tr>
                <tr>
                  <th class="sortable" @click="sortPlanBy('groupLabel')">
                    {{ planGroupColumnLabel }}{{ sortMark('groupLabel') }}
                  </th>
                  <th
                    v-if="planShowsSupplyItem"
                    class="sortable"
                    @click="sortPlanBy('supplyCategory')"
                  >
                    {{ '소모품 기본정보' | translate }}{{ sortMark('supplyCategory') }}
                  </th>
                  <th
                    v-if="planShowsSupplyItem"
                    class="sortable"
                    @click="sortPlanBy('partNumber')"
                  >
                    {{ '카트리지번호/품번' | translate }}{{ sortMark('partNumber') }}
                  </th>
                  <th
                    v-if="search.groupBy === 'SUPPLY' || search.groupBy === 'MODEL'"
                    class="sortable"
                    @click="sortPlanBy('manufacturerName')"
                  >
                    {{ '제조사' | translate }}{{ sortMark('manufacturerName') }}
                  </th>
                  <th v-if="isPlanSkuGroup" class="sortable" @click="sortPlanBy('partNumber')">
                    {{ '품번' | translate }}{{ sortMark('partNumber') }}
                  </th>
                  <th class="sortable num" @click="sortPlanBy('slotCount')">
                    {{ '장착 슬롯 수' | translate }}{{ sortMark('slotCount') }}
                  </th>
                  <th class="sortable num" @click="sortPlanBy('avgRemainPercent')">
                    {{ '평균잔량' | translate }}{{ sortMark('avgRemainPercent') }}
                  </th>
                  <th class="sortable num" @click="sortPlanBy('urgentCount')">
                    {{ '긴급' | translate }}{{ sortMark('urgentCount') }}
                  </th>
                  <th class="sortable num" @click="sortPlanBy('avgCycleDays')">
                    {{ '평균주기(일)' | translate }}{{ sortMark('avgCycleDays') }}
                  </th>
                  <th class="sortable" :class="{ 'horizon-col': search.horizonDays === 30 }" @click="sortPlanBy('qty30')">
                    {{ '30일' | translate }}{{ sortMark('qty30') }}
                  </th>
                  <th class="sortable" :class="{ 'horizon-col': search.horizonDays === 90 }" @click="sortPlanBy('qty90')">
                    {{ '90일' | translate }}{{ sortMark('qty90') }}
                  </th>
                  <th class="sortable" :class="{ 'horizon-col': search.horizonDays === 180 }" @click="sortPlanBy('qty180')">
                    {{ '180일' | translate }}{{ sortMark('qty180') }}
                  </th>
                  <th class="sortable" @click="sortPlanBy('qtyHorizon')">
                    {{ '기간합' | translate }}{{ sortMark('qtyHorizon') }}
                  </th>
                  <th v-if="isSupplyGroup">{{ '공용 품번' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!planList.length">
                  <td :colspan="planColspan" class="empty">{{ '데이터가 없습니다.' | translate }}</td>
                </tr>
                <tr
                  v-for="(row, idx) in planList"
                  :key="'plan-' + (row.groupKey || idx)"
                  class="clickable-row"
                  data-supply-drawer-keep
                  @click="openPlanDrawer(row)"
                >
                  <td>{{ formatGroupLabel(row) }}</td>
                  <td v-if="planShowsSupplyItem">{{ formatPlanItemInfo(row) }}</td>
                  <td v-if="planShowsSupplyItem">{{ row.partNumber || '-' }}</td>
                  <td v-if="search.groupBy === 'SUPPLY' || search.groupBy === 'MODEL'">
                    {{ formatManufacturer(row) }}
                  </td>
                  <td v-if="isPlanSkuGroup">{{ row.partNumber || '-' }}</td>
                  <td class="num">{{ Number(row.slotCount || 0) | currency }}{{ '개' | translate }}</td>
                  <td class="num">{{ formatNullable(row.avgRemainPercent) }}</td>
                  <td class="num">{{ Number(row.urgentCount || 0) | currency }}</td>
                  <td class="num" :class="{ muted: row.avgCycleDays == null || row.avgCycleDays === '' }">
                    {{ formatNullable(row.avgCycleDays) }}
                  </td>
                  <td class="num">{{ row.qty30 || 0 }}</td>
                  <td class="num">{{ row.qty90 || 0 }}</td>
                  <td class="num">{{ row.qty180 || 0 }}</td>
                  <td class="num">{{ row.qtyHorizon || 0 }}</td>
                  <td v-if="isSupplyGroup">
                    <button
                      type="button"
                      class="map-link-btn"
                      data-supply-drawer-keep
                      @click.stop="openCommonPartMap(row)"
                    >
                      {{ '매핑' | translate }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            </div>
            <div class="paging plan-paging">
              <span class="plan-result-count">
                {{ planTotalCount | currency }}{{ '개의 결과를 표시합니다.' | translate }}
              </span>
              <div class="plan-paging-nav">
                <button type="button" class="supply-btn outline" :disabled="search.page <= 1" @click="prevPage">
                  {{ '이전' | translate }}
                </button>
                <span>{{ search.page }} / {{ totalPages }}</span>
                <button
                  type="button"
                  class="supply-btn outline"
                  :disabled="search.page >= totalPages"
                  @click="nextPage"
                >
                  {{ '다음' | translate }}
                </button>
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="activeTab === 'common-part'">
          <supply-common-part-panel
            :refresh-key="commonPartRefreshKey"
            @edit="openCommonPartMap"
            @deleted="onCommonPartSaved"
          />
        </template>
      </div>
    </template>
  </main-layout>
</template>

<script>
import MainLayout from '@/components/common/layout/MainLayout'
import MainHeader from '@/components/common/layout/MainHeader'
import SupplyReplaceHistoryDrawer from '@/views/monitoring/supply/SupplyReplaceHistoryDrawer'
import SupplySkuInsightPanel from '@/views/monitoring/supply/SupplySkuInsightPanel'
import SupplyEfficiencyPanel from '@/views/monitoring/supply/SupplyEfficiencyPanel'
import SupplySalesIntelPanel from '@/views/monitoring/supply/SupplySalesIntelPanel'
import SupplyCommonPartDrawer from '@/views/monitoring/supply/SupplyCommonPartDrawer'
import SupplyCommonPartPanel from '@/views/monitoring/supply/SupplyCommonPartPanel'
import MenuBetaBadge from '@/components/common/MenuBetaBadge.vue'
import { PATH } from '@/constants'
import NumberFormatUtil from '@/assets/plugins/mps-common/formatters/NumberFormatUtil'
import { downloadSupplyExcel, supplyExcelStamp } from '@/excel/downloadSupplyExcel'

function toYmd(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function defaultToday() {
  return toYmd(new Date())
}

function defaultThirtyDayRange() {
  const to = new Date()
  to.setHours(0, 0, 0, 0)
  const from = new Date(to)
  from.setDate(to.getDate() - 29)
  return { fromDate: toYmd(from), toDate: toYmd(to) }
}

export default {
  name: 'SupplyStatus',
  components: {
    MainLayout,
    MainHeader,
    MenuBetaBadge,
    SupplyReplaceHistoryDrawer,
    SupplySkuInsightPanel,
    SupplyEfficiencyPanel,
    SupplySalesIntelPanel,
    SupplyCommonPartDrawer,
    SupplyCommonPartPanel,
  },
  mixins: [NumberFormatUtil],
  data() {
    return {
      activeTab: 'dashboard',
      noticeDetail: false,
      reloadInFlight: false,
      commonPartOpen: false,
      commonPartContext: {},
      commonPartRefreshKey: 0,
      contentTabs: [
        { value: 'dashboard', label: '대시보드' },
        { value: 'sales', label: '영업' },
        { value: 'status', label: '소모품 현황' },
        { value: 'efficiency', label: '출력 효율' },
        { value: 'plan', label: '소모품 교체 계획' },
        { value: 'common-part', label: '공용 품번' },
      ],
      statusView: 'now',
      statusViewTabs: [
        { value: 'now', label: '지금' },
        { value: 'compare', label: '비교' },
        { value: 'forecast', label: '예측' },
      ],
      statusIncludeUnmapped: true,
      periodPreset: '30d',
      periodPresets: [
        { key: 'today', label: '당일' },
        { key: 'yesterday', label: '전일' },
        { key: '7d', label: '7일' },
        { key: '30d', label: '30일' },
        { key: '90d', label: '90일' },
        { key: '180d', label: '180일' },
        { key: 'thisMonth', label: '이번달' },
        { key: 'lastMonth', label: '지난달' },
        { key: 'custom', label: '직접입력' },
      ],
      search: {
        rentalcompanyId: null,
        fromDate: defaultThirtyDayRange().fromDate,
        toDate: defaultThirtyDayRange().toDate,
        supplyCategory: '',
        partNumber: '',
        manufacturerName: '',
        keyword: '',
        colorCode: '',
        groupBy: 'SUPPLY',
        searchType: 'AUTO',
        partNumberExact: '',
        printerModelId: null,
        groupKey: '',
        trendBucket: 'DAY',
        orderBy: 'replaceCount',
        orderDir: 'DESC',
        excludePaperRelated: true,
        page: 1,
        rows: 30,
        horizonDays: 30,
        cycleLookbackDays: 180,
        urgentOnly: false,
        unmappedOnly: false,
      },
      unmappedCount: 0,
      unmappedCountLoaded: false,
      unmappedCountTimer: null,
      planOverview: {
        urgentCount: 0,
        qty30: 0,
        qty60: 0,
        qty90: 0,
        qty180: 0,
        qtyHorizon: 0,
        skuCount: 0,
        predictedSlotCount: 0,
        insufficientCount: 0,
        byCategory: [],
        byManufacturer: [],
      },
      planList: [],
      planLoaded: false,
      planTotalCount: 0,
      horizonPresets: [30, 60, 90, 180],
      skuKeyword: '',
      skuPage: 1,
      skuRows: 30,
      skuOrderBy: 'errorCount',
      statusCardLowEmpty: false,
      skuOrderDir: 'DESC',
      skuTotalCount: 0,
      skuInsightTotals: { errorCount: 0, lowEmptyCount: 0, urgentCount: 0 },
      statusSummary: [],
      skuUsageList: [],
      skuPlanList: [],
      currentRows: [],
      currentKeyword: '',
      currentColorCode: '',
      currentStatusCode: '',
      currentRemainMax: '',
      currentRemainBucket: '',
      currentCategory: '',
      currentManufacturer: '',
      currentPage: 1,
      currentRowsPerPage: 30,
      currentOrderBy: 'activeCount',
      currentOrderDir: 'DESC',
      currentTotalCount: 0,
      currentAsset: {
        totals: {},
        byCategory: [],
        byManufacturer: [],
        byStatus: [],
        byRemain: [],
      },
      overview: {
        totalReplaceCount: 0,
        lowEmptyPrinterCount: 0,
        lowEmptyItemCount: 0,
        forecastItemCount: 0,
        errorCount: 0,
      },
      usageList: [],
      accountReplaceRows: [],
      usageLoaded: false,
      categoryBreakdown: [],
      manufacturerBreakdown: [],
      breakdownLoaded: false,
      categoryQuery: '',
      manufacturerQuery: '',
      categorySort: { key: 'replaceCount', dir: 'DESC' },
      manufacturerSort: { key: 'replaceCount', dir: 'DESC' },
      totalCount: 0,
      trendRows: [],
      forecastList: [],
      forecastLoaded: false,
      rentalcompanyOptions: [],
      colorOptions: ['BLACK', 'CYAN', 'MAGENTA', 'YELLOW', 'COLOR', 'UNKNOWN'],
      groupTabs: [
        { value: 'SUPPLY', label: '소모품별' },
        { value: 'MODEL', label: '프린터 모델별' },
        { value: 'RENTAL', label: '임대업체별' },
      ],
      historyDrawerOpen: false,
      historyShowExcel: true,
      historyDrawerTitle: '',
      historyDrawerMode: 'history',
      historyQuery: {},
      salesKeyword: '',
      salesSearchType: 'AUTO',
      salesSuggestions: [],
      salesSuggestTimer: null,
      salesRows: [],
      salesLoaded: false,
      salesTotalCount: 0,
      salesDrill: null,
      salesKpis: {
        replaceCount: 0,
        lowEmptyCount: 0,
        errorCount: 0,
        urgentCount: 0,
        slotCount: 0,
        compatibleSlotCount: 0,
        oemSlotCount: 0,
        compatRatio: null,
      },
    }
  },
  computed: {
    totalPages() {
      const count = this.activeTab === 'plan' ? this.planTotalCount : this.totalCount
      return Math.max(1, Math.ceil((count || 0) / this.search.rows))
    },
    statusCategoryCards() {
      const order = ['TONER', 'INK', 'DRUM', 'WASTE', 'OTHER']
      return (this.currentAsset.byCategory || []).slice().sort((a, b) => {
        const ia = order.indexOf(a.itemKey)
        const ib = order.indexOf(b.itemKey)
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib)
      })
    },
    statusCategoryCardItems() {
      return this.statusCategoryCards.map((item) => ({
        itemKey: item.itemKey,
        label: this.formatCategory(item.itemKey),
        lowEmpty: this.typeLowEmpty(item),
        printerCount: Number(item.printerCount || 0),
        slotCount: Number(item.slotCount || 0),
      }))
    },
    skuInsightRows() {
      const usageByKey = {}
      this.skuUsageList.forEach((row) => {
        const key = this.skuMergeKey(row)
        if (key && !usageByKey[key]) usageByKey[key] = row
      })
      const planByKey = {}
      this.skuPlanList.forEach((row) => {
        const key = this.skuMergeKey(row)
        if (key && !planByKey[key]) planByKey[key] = row
      })
      const source = this.statusSummary.length ? this.statusSummary : this.skuUsageList
      return source
        .map((row, idx) => {
          const key = this.skuMergeKey(row) || `sku-${idx}`
          const usage = usageByKey[key] || {}
          const plan = planByKey[key] || {}
          return {
            ...usage,
            ...plan,
            ...row,
            rowKey: key,
            familyPartNumber: row.partNumber || usage.partNumber || plan.partNumber || '',
            replaceCount: row.replaceCount != null ? row.replaceCount : usage.replaceCount,
            avgCycleDays:
              row.avgCycleDays != null ? row.avgCycleDays : usage.avgCycleDays || plan.avgCycleDays,
            monthlyForecast:
              row.monthlyForecast != null ? row.monthlyForecast : usage.monthlyForecast,
            qtyHorizon: plan.qtyHorizon != null ? plan.qtyHorizon : row.qtyHorizon,
            urgentCount: row.urgentCount != null ? row.urgentCount : plan.urgentCount,
          }
        })
        .filter((row) =>
          this.isSupplyGroup && !this.statusIncludeUnmapped
            ? String(row.partNumber || '').trim()
            : true,
        )
    },
    usageColspan() {
      return this.search.groupBy === 'SUPPLY' ? 13 : 8
    },
    hasGroupTabs() {
      return ['status', 'plan'].indexOf(this.activeTab) >= 0
    },
    noticeLead() {
      if (this.activeTab === 'plan') return this.t('교체 계획 조회 기준')
      return this.t('수집 데이터 및 제외 대상 안내')
    },
    noticeBody() {
      if (this.activeTab === 'plan') {
        return this.t(
          '전일 슬롯 스냅샷 기준입니다. 잔량으로 다음 교체를 보고, 평균주기(약 180일·14~365일)가 있으면 기간 안 반복 교체를 더합니다. 용지 포함·다른 주기 일수·미매핑만 보면 라이브로 집계합니다.'
        )
      }
      return this.t(
        '프린터 칩 오류 또는 사용량 수집 시 시스템 오류 등으로 데이타에 결함이 있을 수 있습니다. 무한잉크로 설정된 프린터는 소모품 교체 현황에 포함되지 않습니다. 동일 시리얼·다중 IP 수집으로 인한 오탐 교체는 현황에 포함되지 않습니다.'
      )
    },
    showPeriodToolbar() {
      if (this.activeTab === 'plan' || this.activeTab === 'common-part') return false
      return true
    },
    showHorizonToolbar() {
      return this.activeTab === 'plan'
    },
    statusHint() {
      return '현재 슬롯과 선택 기간의 교체·예측을 한 목록에서 봅니다. 숫자를 누르면 해당 대상이 우측 목록으로 열립니다.'
    },
    isSupplyGroup() {
      return this.search.groupBy === 'SUPPLY'
    },
    planShowsSupplyItem() {
      return this.search.groupBy === 'MODEL' || this.search.groupBy === 'ACCOUNT'
    },
    efficiencyOverviewPath() {
      return PATH.MONITORING.SUPPLY_EFFICIENCY_OVERVIEW
    },
    efficiencyRankingPath() {
      return PATH.MONITORING.SUPPLY_EFFICIENCY_RANKING
    },
    efficiencyCyclesPath() {
      return PATH.MONITORING.SUPPLY_EFFICIENCY_CYCLES
    },
    usageGroupColumnLabel() {
      if (this.search.groupBy === 'MODEL') return this.t('프린터 모델')
      if (this.search.groupBy === 'RENTAL') return this.t('임대업체')
      return this.t('구분')
    },
    isPlanSkuGroup() {
      return false
    },
    planBasicColspan() {
      if (this.search.groupBy === 'MODEL') return 4
      if (this.search.groupBy === 'ACCOUNT') return 3
      if (this.search.groupBy === 'SUPPLY') return 2
      return 1
    },
    planColspan() {
      if (this.search.groupBy === 'RENTAL') return 9
      if (this.search.groupBy === 'MODEL') return 12
      if (this.search.groupBy === 'ACCOUNT') return 11
      if (this.search.groupBy === 'SUPPLY') return 11
      return 10
    },
    planGroupColumnLabel() {
      if (this.search.groupBy === 'MODEL') return this.t('프린터 모델')
      if (this.search.groupBy === 'RENTAL') return this.t('임대업체')
      return this.t('소모품 기본정보')
    },
    planListTitle() {
      if (this.search.groupBy === 'MODEL') return this.t('프린터 모델별 교체 예상 수량')
      if (this.search.groupBy === 'RENTAL') return this.t('임대업체별 교체 예상 수량')
      return this.t('소모품 기본정보·제조사별 교체 예상 수량')
    },
    planTargetLabel() {
      if (this.search.groupBy === 'MODEL') return this.t('대상 모델')
      if (this.search.groupBy === 'RENTAL') return this.t('대상 임대업체')
      return this.t('대상 구분')
    },
    planCategoryQtyTotal() {
      return (this.planOverview.byCategory || []).reduce(
        (sum, item) => sum + Number(item.qtyHorizon || 0),
        0
      )
    },
    planManufacturerQtyTotal() {
      return (this.planOverview.byManufacturer || []).reduce(
        (sum, item) => sum + Number(item.qtyHorizon || 0),
        0
      )
    },
    planSearchPlaceholder() {
      if (this.search.groupBy === 'MODEL') return this.t('프린터 모델 / 제조사 검색')
      if (this.search.groupBy === 'RENTAL') return this.t('임대업체 검색')
      return this.t('기본정보 / 제조사 / 품번 검색')
    },
    trendLabels() {
      const map = {}
      this.trendRows.forEach((r) => {
        map[r.bucket] = (map[r.bucket] || 0) + (r.replaceCount || 0)
      })
      return Object.keys(map).sort()
    },
    trendValues() {
      return this.trendLabels.map((b) =>
        this.trendRows
          .filter((r) => r.bucket === b)
          .reduce((sum, r) => sum + (r.replaceCount || 0), 0)
      )
    },
    maxTrend() {
      return Math.max(1, ...this.trendValues, 1)
    },
    categoryReplaceTotal() {
      return this.categoryBreakdown.reduce((sum, r) => sum + Number(r.replaceCount || 0), 0)
    },
    manufacturerReplaceTotal() {
      return this.manufacturerBreakdown.reduce((sum, r) => sum + Number(r.replaceCount || 0), 0)
    },
    filteredCategoryBreakdown() {
      return this.filterSortBreakdown(
        this.categoryBreakdown,
        this.categoryQuery,
        this.categorySort,
        (item) => `${item.itemKey || ''} ${this.formatCategory(item.itemKey)}`
      )
    },
    filteredManufacturerBreakdown() {
      return this.filterSortBreakdown(
        this.manufacturerBreakdown,
        this.manufacturerQuery,
        this.manufacturerSort,
        (item) => item.itemKey || ''
      )
    },
    usageSearchPlaceholder() {
      if (this.search.groupBy === 'MODEL') {
        return this.t('프린터 모델 / 제조사 검색')
      }
      if (this.search.groupBy === 'RENTAL') {
        return this.t('임대업체 검색')
      }
      return this.t('품번 / 설명 / 제조사 / 모델 / 색상 검색')
    },
    hasUsageListFilter() {
      return !!(
        this.search.keyword ||
        this.search.colorCode ||
        this.search.supplyCategory ||
        this.search.manufacturerName
      )
    },
    hasSalesFilter() {
      return !!(
        this.salesKeyword ||
        this.salesDrill ||
        this.search.manufacturerName ||
        this.search.partNumberExact ||
        this.search.printerModelId ||
        this.search.groupKey
      )
    },
    salesTotalPages() {
      return Math.max(1, Math.ceil((this.salesTotalCount || 0) / this.search.rows))
    },
  },
  created() {
    this.loadRentalcompanies()
    this.reload()
  },
  watch: {
    statusIncludeUnmapped() {
      if (this.activeTab === 'status') {
        this.skuPage = 1
        this.loadSkuInsight()
      }
    },
  },
  beforeDestroy() {
    if (this.unmappedCountTimer) {
      clearTimeout(this.unmappedCountTimer)
      this.unmappedCountTimer = null
    }
    if (this.salesSuggestTimer) {
      clearTimeout(this.salesSuggestTimer)
      this.salesSuggestTimer = null
    }
  },
  methods: {
    t(text) {
      return this.$options.filters && this.$options.filters.translate
        ? this.$options.filters.translate(text)
        : text
    },
    revealHistory(showExcel = true) {
      this.historyShowExcel = showExcel !== false
      this.historyDrawerOpen = true
    },
    openAccountDetail(item) {
      if (!item?.accountId) return
      this.$router.push({
        name: 'managertnbox.account.management',
        query: {
          accountId: item.accountId,
          rentalcompanyId: item.rentalcompanyId || undefined,
        },
      })
    },
    openPrinterDetail(item) {
      if (!item?.tnDeviceId || !item?.rentalcompanyId) return
      this.$router.push({
        name: 'managertnbox.monitoring.device.list.detail.info',
        query: {
          rentalcompanyId: item.rentalcompanyId,
          tnDeviceId: item.tnDeviceId,
          accountId: item.accountId,
        },
      })
    },
    barHeight(val) {
      return Math.max(4, Math.round((val / this.maxTrend) * 120))
    },
    formatNullable(val) {
      if (val === null || val === undefined || val === '') return this.t('데이터 부족')
      return val
    },
    formatCompatibilityType(type) {
      const key = String(type || '').toUpperCase()
      if (key === 'OEM' || key === 'EXACT' || key === 'GENUINE') return this.t('정품')
      if (key === 'COMPATIBLE' || key === 'COMPAT') return this.t('호환')
      if (key === 'REMAN' || key === 'REMANUFACTURED' || key === 'REFILL') return this.t('재생')
      return this.t('미지정')
    },
    benchmarkBadge(compare) {
      const key = String(compare || '').toUpperCase()
      if (key === 'HIGHER') return this.t('높음')
      if (key === 'LOWER') return this.t('낮음')
      if (key === 'SIMILAR') return this.t('유사')
      return ''
    },
    benchmarkTooltip(row, kind) {
      const compare =
        kind === 'cycle'
          ? row.cycleCompare
          : kind === 'efficiency'
            ? row.efficiencyCompare
            : row.forecastCompare
      if (!compare || compare === 'NA') {
        return this.t('전체 벤치마크 비교 불가 (유형 미지정·샘플 부족·배치 미실행)')
      }
      const local =
        kind === 'cycle'
          ? this.formatNullable(row.avgCycleDays)
          : kind === 'efficiency'
            ? this.formatNullable(row.pagesPerCycleA4)
            : this.formatNullable(row.monthlyForecast)
      const global =
        kind === 'cycle'
          ? this.formatNullable(row.globalAvgCycleDays)
          : kind === 'efficiency'
            ? this.formatNullable(row.globalPagesPerCycleA4)
            : this.formatNullable(row.globalMonthlyForecast)
      const diff =
        kind === 'cycle'
          ? row.cycleDiffDays
          : kind === 'efficiency'
            ? row.efficiencyDiff
            : row.forecastDiff
      const pct =
        kind === 'cycle'
          ? row.cycleDiffPct
          : kind === 'efficiency'
            ? row.efficiencyDiffPct
            : row.forecastDiffPct
      const diffText =
        diff == null
          ? ''
          : ` (${diff > 0 ? '+' : ''}${Number(diff).toLocaleString()}${
              pct == null ? '' : `, ${pct > 0 ? '+' : ''}${Number(pct).toLocaleString()}%`
            })`
      const dateText = row.benchmarkStatDate ? ` · ${row.benchmarkStatDate}` : ''
      return `${this.t('당사')} ${local} / ${this.t('전체')} ${global}${diffText}${dateText}`
    },
    filterStatusCategory(key) {
      const next = this.search.supplyCategory === key ? '' : key || ''
      this.search.supplyCategory = next
      this.statusCardLowEmpty = !!next
      this.skuPage = 1
      this.loadSkuInsight()
    },
    typeLowEmpty(item) {
      return Number(item && item.lowCount || 0) + Number(item && item.emptyCount || 0)
    },
    formatCategory(cat) {
      const map = {
        TONER: this.t('토너'),
        INK: this.t('잉크'),
        DRUM: this.t('드럼'),
        WASTE: this.t('폐토너'),
        OTHER: this.t('기타'),
      }
      return map[cat] || cat || '-'
    },
    formatColorCode(color) {
      if (!color || String(color).toUpperCase() === 'UNKNOWN') return ''
      return color
    },
    skuMergeKey(row) {
      if (!this.isSupplyGroup) {
        return (
          row?.groupKey ||
          (row?.printerModelId != null ? String(row.printerModelId) : '') ||
          (row?.rentalcompanyId != null ? String(row.rentalcompanyId) : '') ||
          row?.rentalcompanyName ||
          row?.printerModelName ||
          ''
        )
      }
      return row?.marketItemKey || row?.partNumber || row?.groupKey || ''
    },
    formatSupplyInfo(row) {
      const desc = String(row?.description || '').trim()
      const label = String(row?.supplyLabel || '').trim()
      return desc || label || this.formatCategory(row?.supplyCategory)
    },
    formatPlanItemInfo(row) {
      const cat = this.formatCategory(row.supplyCategory, row.description)
      const color = this.formatColorCode(row.colorCode)
      return [cat, color].filter(Boolean).join(' ') || row.description || '-'
    },
    formatPlanRowLabel(row) {
      if (this.activeTab === 'plan' && this.search.groupBy === 'SUPPLY') {
        return `${this.formatSupplyInfo(row)} · ${this.formatManufacturer(row)}`
      }
      if (this.activeTab === 'plan' && this.planShowsSupplyItem) {
        const part = row.partNumber ? ` (${row.partNumber})` : ''
        return `${this.formatGroupLabel(row)} · ${this.formatPlanItemInfo(row)}${part}`
      }
      return this.formatGroupLabel(row)
    },
    formatGroupLabel(row) {
      if (this.activeTab === 'plan' && this.search.groupBy === 'SUPPLY') {
        return this.formatSupplyInfo(row)
      }
      if (this.search.groupBy !== 'SUPPLY') {
        return row.groupLabel || row.supplyLabel || row.rentalcompanyName || row.printerModelName || '-'
      }
      const cat = this.formatCategory(row.supplyCategory)
      const color =
        row.colorCode && String(row.colorCode).toUpperCase() !== 'UNKNOWN'
          ? ` ${row.colorCode}`
          : ''
      const part = row.partNumber ? ` (${row.partNumber})` : ''
      if (row.supplyCategory || row.colorCode || row.partNumber) {
        return `${cat}${color}${part}`
      }
      return row.groupLabel || row.supplyLabel || '-'
    },
    formatManufacturer(row) {
      const name = String(row?.manufacturerName || '').trim()
      if (!name || name.toUpperCase() === 'UNKNOWN') return this.t('미지정')
      return name
    },
    formatPrinterModel(row) {
      const name = row.printerModelName || ''
      const extra = Number(row.printerModelCount || 0)
      if (!name) return extra > 1 ? `${this.t('외')} ${extra}` : '-'
      if (extra > 1) return `${name} ${this.t('외')} ${extra - 1}`
      return name
    },
    payload(extra = {}) {
      return {
        rentalcompanyId: this.search.rentalcompanyId || null,
        fromDate: this.search.fromDate,
        toDate: this.search.toDate,
        supplyCategory: this.search.supplyCategory || null,
        partNumber: this.search.partNumber || null,
        manufacturerName: this.search.manufacturerName || null,
        keyword: this.search.keyword || null,
        colorCode: this.search.colorCode || null,
        groupBy: this.search.groupBy,
        trendBucket: this.search.trendBucket,
        trendTotalOnly: true,
        orderBy: this.search.orderBy,
        orderDir: this.search.orderDir,
        excludePaperRelated: !!this.search.excludePaperRelated,
        unmappedOnly: !!this.search.unmappedOnly,
        horizonDays: this.search.horizonDays,
        cycleLookbackDays: this.search.cycleLookbackDays,
        urgentOnly: !!this.search.urgentOnly,
        page: this.search.page,
        rows: this.search.rows,
        ...extra,
      }
    },
    changeStatusView(view) {
      this.statusView = view
      this.search.page = 1
      if (view === 'compare') {
        this.skuOrderBy = 'errorCount'
        this.skuOrderDir = 'DESC'
      } else if (view === 'forecast') {
        this.skuOrderBy = 'replaceCount'
        this.skuOrderDir = 'DESC'
      }
      this.loadStatusTab()
    },
    async loadStatusTab() {
      await Promise.all([this.loadSkuInsight(), this.loadCurrentAsset()])
    },
    changeContentTab(tab) {
      if (tab === 'sku' || tab === 'current' || tab === 'usage') {
        this.activeTab = 'status'
        this.loadStatusTab()
        return
      }
      this.activeTab = tab
      this.noticeDetail = false
      if (tab === 'dashboard') {
        if (!this.trendRows.length) this.loadTrend()
        if (!this.breakdownLoaded) this.loadBreakdown()
        return
      }
      if (tab === 'sales') {
        if (this.search.orderBy === 'qtyHorizon') {
          this.search.orderBy = 'replaceCount'
          this.search.orderDir = 'DESC'
        }
        this.loadSales()
        return
      }
      if (tab === 'status') {
        this.loadStatusTab()
        return
      }
      if (tab === 'efficiency') {
        return
      }
      if (tab === 'plan') {
        if (this.search.orderBy === 'replaceCount') {
          this.search.orderBy = 'qtyHorizon'
          this.search.orderDir = 'DESC'
        }
        this.planLoaded = false
        this.loadPlan()
        return
      }
      if (tab === 'common-part') {
        return
      }
    },
    applyPreset(key) {
      this.periodPreset = key
      const today = new Date()
      const end = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      let start = new Date(end)
      let bucket = this.search.trendBucket
      if (key === 'today') {
        start = new Date(end)
        bucket = 'DAY'
      } else if (key === 'yesterday') {
        end.setDate(end.getDate() - 1)
        start = new Date(end)
        bucket = 'DAY'
      } else if (key === '7d') {
        start.setDate(end.getDate() - 6)
        bucket = 'DAY'
      } else if (key === '30d') {
        start.setDate(end.getDate() - 29)
        bucket = 'DAY'
      } else if (key === '90d') {
        start.setDate(end.getDate() - 89)
        bucket = 'WEEK'
      } else if (key === '180d') {
        start.setDate(end.getDate() - 179)
        bucket = 'MONTH'
      } else if (key === 'thisMonth') {
        start = new Date(end.getFullYear(), end.getMonth(), 1)
        bucket = 'DAY'
      } else if (key === 'lastMonth') {
        start = new Date(end.getFullYear(), end.getMonth() - 1, 1)
        end.setDate(0)
        bucket = 'DAY'
      } else {
        return
      }
      this.search.fromDate = toYmd(start)
      this.search.toDate = toYmd(end)
      this.search.trendBucket = bucket
      this.reload()
    },
    onCustomDate() {
      this.periodPreset = 'custom'
    },
    toggleExcludePaper() {
      this.search.excludePaperRelated = !this.search.excludePaperRelated
      this.reload()
    },
    toggleOrderDir() {
      this.search.orderDir = this.search.orderDir === 'ASC' ? 'DESC' : 'ASC'
      this.reloadListOnly()
    },
    sortBy(field) {
      if (this.search.orderBy === field) {
        this.toggleOrderDir()
        return
      }
      this.search.orderBy = field
      this.search.orderDir = 'DESC'
      this.reloadListOnly()
    },
    sortMark(field) {
      if (this.search.orderBy !== field) return ''
      return this.search.orderDir === 'ASC' ? ' ▲' : ' ▼'
    },
    sortBreakdown(kind, key) {
      const sort = kind === 'category' ? this.categorySort : this.manufacturerSort
      if (sort.key === key) {
        sort.dir = sort.dir === 'ASC' ? 'DESC' : 'ASC'
      } else {
        sort.key = key
        sort.dir = key === 'replaceCount' ? 'DESC' : 'ASC'
      }
    },
    breakdownSortMark(kind, key) {
      const sort = kind === 'category' ? this.categorySort : this.manufacturerSort
      if (sort.key !== key) return ''
      return sort.dir === 'ASC' ? ' ▲' : ' ▼'
    },
    async downloadBreakdownExcel(kind) {
      const isCategory = kind === 'category'
      const rows = isCategory ? this.filteredCategoryBreakdown : this.filteredManufacturerBreakdown
      if (!rows.length) {
        this.$toasted?.error?.(this.t('내려받을 데이터가 없습니다.'))
        return
      }
      const title = this.t(isCategory ? '구분별 교체수' : '제조사별 교체수')
      try {
        await downloadSupplyExcel({
          title,
          fileName: `${title}_${supplyExcelStamp()}.xlsx`,
          columns: [
            {
              header: this.t('항목'),
              key: 'itemKey',
              width: 24,
              value: (row) =>
                isCategory ? this.formatCategory(row.itemKey) : row.itemKey || '-',
            },
            {
              header: this.t('교체수'),
              key: 'replaceCount',
              width: 12,
              value: (row) => Number(row.replaceCount || 0),
            },
          ],
          rows,
        })
      } catch (e) {
        console.error(e)
        this.$toasted?.error?.(this.t('엑셀 다운로드에 실패하였습니다.'))
      }
    },
    filterSortBreakdown(list, query, sort, textFn) {
      const q = String(query || '').trim().toLowerCase()
      const filtered = (list || []).filter((item) => {
        if (!q) return true
        return String(textFn(item)).toLowerCase().includes(q)
      })
      const dir = sort.dir === 'ASC' ? 1 : -1
      return filtered.slice().sort((a, b) => {
        const av = a[sort.key]
        const bv = b[sort.key]
        if (sort.key === 'replaceCount') {
          return (Number(av || 0) - Number(bv || 0)) * dir
        }
        return String(av || '').localeCompare(String(bv || ''), 'ko') * dir
      })
    },
    clearUsageListFilter() {
      this.search.keyword = ''
      this.search.colorCode = ''
      this.search.supplyCategory = ''
      this.search.manufacturerName = ''
      this.reloadListOnly()
    },
    async reload() {
      if (this.reloadInFlight) return
      this.reloadInFlight = true
      this.search.page = 1
      this.skuPage = 1
      this.currentPage = 1
      this.usageLoaded = false
      this.forecastLoaded = false
      this.breakdownLoaded = false
      this.planLoaded = false
      this.salesLoaded = false
      this.usageList = []
      this.forecastList = []
      this.planList = []
      this.trendRows = []
      this.categoryBreakdown = []
      this.manufacturerBreakdown = []
      try {
        if (this.activeTab === 'dashboard') {
          await Promise.all([
            this.loadOverview(),
            this.loadTrend(),
            this.loadBreakdown(),
            this.loadAccountReplace(),
          ])
          return
        }
        if (this.activeTab === 'sales') {
          await this.loadSales()
          return
        }
        if (this.activeTab === 'status') {
          await this.loadStatusTab()
          return
        }
        if (this.activeTab === 'plan') {
          await this.loadPlan()
          return
        }
        if (this.activeTab === 'usage') {
          await Promise.all([this.loadOverview(), this.loadUsageList(), this.loadBreakdown()])
          return
        }
        if (this.activeTab === 'common-part') {
          this.commonPartRefreshKey += 1
          return
        }
        await Promise.all([this.loadOverview(), this.loadForecast()])
      } finally {
        this.scheduleUnmappedCount()
        this.reloadInFlight = false
      }
    },
    reloadListOnly() {
      this.search.page = 1
      this.loadUsageList()
    },
    openBreakdownDrawer(kind, item) {
      const key = item && item.itemKey
      this.historyDrawerMode = 'history'
      this.historyDrawerTitle =
        kind === 'category'
          ? `${this.formatCategory(key)} · ${this.t('교체 내역')}`
          : `${key || '-'} · ${this.t('교체 내역')}`
      this.historyQuery = {
        rentalcompanyId: this.search.rentalcompanyId || null,
        fromDate: this.search.fromDate,
        toDate: this.search.toDate,
        partNumber: this.search.partNumber || '',
        excludePaperRelated: !!this.search.excludePaperRelated,
        supplyCategory: kind === 'category' ? key : '',
        manufacturerName: kind === 'manufacturer' ? key : null,
      }
      this.revealHistory()
    },
    changeGroup(groupBy) {
      this.search.groupBy = groupBy
      this.search.page = 1
      this.skuPage = 1
      this.currentPage = 1
      this.usageLoaded = false
      this.planLoaded = false
      if (this.activeTab === 'status') {
        this.loadStatusTab()
        return
      }
      if (this.activeTab === 'plan') {
        this.planLoaded = false
        this.loadPlan()
      }
    },
    openDashboardDrawer(mode) {
      const count =
        mode === 'lowEmpty'
          ? this.overview.lowEmptyItemCount
          : mode === 'forecast'
            ? this.overview.forecastItemCount
            : mode === 'error'
              ? this.overview.errorCount
              : this.overview.totalReplaceCount
      const label =
        mode === 'lowEmpty'
          ? this.t('LOW/EMPTY 슬롯')
          : mode === 'forecast'
            ? this.t('예측 가능 품목')
            : mode === 'error'
              ? this.t('에러 슬롯')
              : this.t('기간 총 교체')
      this.historyDrawerMode = mode === 'error' ? 'error' : mode
      this.historyDrawerTitle = `${label} (${Number(count || 0).toLocaleString()})`
      this.historyQuery =
        mode === 'error'
          ? this.dashboardErrorSlotQuery()
          : mode === 'lowEmpty'
            ? this.dashboardLowEmptySlotQuery()
            : {
              rentalcompanyId: this.search.rentalcompanyId || null,
              fromDate: this.search.fromDate,
              toDate: this.search.toDate,
              supplyCategory: this.search.supplyCategory || '',
              partNumber: this.search.partNumber || '',
              colorCode: this.search.colorCode || '',
              excludePaperRelated: !!this.search.excludePaperRelated,
              unmappedOnly: !!this.search.unmappedOnly,
            }
      this.revealHistory(false)
    },
    dashboardPeriodQuery() {
      return {
        rentalcompanyId: this.search.rentalcompanyId || null,
        fromDate: this.search.fromDate,
        toDate: this.search.toDate,
        supplyCategory: this.search.supplyCategory || '',
        partNumber: this.search.partNumber || '',
        colorCode: this.search.colorCode || '',
        excludePaperRelated: !!this.search.excludePaperRelated,
        unmappedOnly: !!this.search.unmappedOnly,
      }
    },
    dashboardLowEmptySlotQuery() {
      return {
        ...this.dashboardPeriodQuery(),
        slotDetail: true,
        lightQuery: true,
        statusCodes: ['LOW', 'EMPTY'],
        requirePartNumber: false,
        excludeNegativeRemainError: !this.search.negativeRemainErrorOnly,
        negativeRemainErrorOnly: !!this.search.negativeRemainErrorOnly,
      }
    },
    dashboardErrorSlotQuery() {
      return {
        ...this.dashboardPeriodQuery(),
        slotDetail: true,
        includeError: true,
        statusCodes: ['ERROR'],
        excludeNegativeRemainError: true,
      }
    },
    pageTotal(res) {
      const data = res?.data !== undefined ? res.data : res
      if (Array.isArray(data)) return data.length
      return Number(data?.totalCount != null ? data.totalCount : 0)
    },
    async applyDashboardListCounts() {
      const period = this.dashboardPeriodQuery()
      const [historyRes, lowRes, forecastRes, errorRes] = await Promise.all([
        this.$request(PATH.MONITORING.SUPPLY_USAGE_HISTORY)
          .setObject({ ...period, page: 1, rows: 1 })
          .enqueue(),
        this.$request(PATH.MONITORING.SUPPLY_STATUS_SUMMARY)
          .setObject({
            ...period,
            lowEmptyOnly: true,
            page: 1,
            rows: 1,
          })
          .enqueue(),
        this.$request(PATH.MONITORING.SUPPLY_PURCHASE_FORECAST)
          .setObject({
            ...period,
            groupBy: 'SUPPLY',
            page: 1,
            rows: 1,
          })
          .enqueue(),
        this.$request(PATH.MONITORING.SUPPLY_REPLACE_PLAN_SLOTS)
          .setObject({
            ...this.dashboardErrorSlotQuery(),
            liveSlotQuery: true,
            page: 1,
            rows: 1,
          })
          .enqueue(),
      ])
      this.overview.totalReplaceCount = this.pageTotal(historyRes)
      this.overview.lowEmptyItemCount = this.parseStatusSummary(lowRes).lowEmptyCount
      this.overview.forecastItemCount = this.pageTotal(forecastRes)
      this.overview.errorCount = this.pageTotal(errorRes)
    },
    openRowHistory(row) {
      this.openSupplyDetail(row)
    },
    openSupplyDetail(row) {
      const query = {
        rentalcompanyId: this.search.rentalcompanyId || null,
        fromDate: this.search.fromDate,
        toDate: this.search.toDate,
        excludePaperRelated: !!this.search.excludePaperRelated,
        unmappedOnly: this.activeTab !== 'sku' && !!this.search.unmappedOnly,
        manufacturerName: this.search.manufacturerName || null,
        colorCode: this.search.colorCode || null,
        keyword: this.search.keyword || null,
        lightQuery: this.activeTab === 'status',
      }
      if (this.search.groupBy === 'MODEL' && row.printerModelId) {
        query.printerModelId = row.printerModelId
      } else if (this.search.groupBy === 'RENTAL' && (row.rentalcompanyId || this.search.rentalcompanyId)) {
        query.rentalcompanyId = row.rentalcompanyId || this.search.rentalcompanyId || null
      } else if (this.activeTab === 'status' && String(row.partNumber || '').trim()) {
        query.partNumberExact = String(row.partNumber).trim()
      } else if (
        row.groupKey &&
        !String(row.groupKey).startsWith('SKU|') &&
        !String(row.groupKey).startsWith('UNMAPPED|')
      ) {
        query.groupKey = row.groupKey
      } else {
        query.partNumberExact = String(row.partNumber || '').trim()
        query.supplyCategory = row.supplyCategory || null
        query.colorCode = row.colorCode || null
      }
      this.historyDrawerMode = 'history'
      const baseTitle = this.formatGroupLabel(row) || row.groupLabel || this.t('교체 내역')
      const modelBit = [row.manufacturerName, this.formatPrinterModel(row)]
        .filter((v) => v && v !== '-')
        .join(' ')
      this.historyDrawerTitle =
        !row.partNumber && modelBit ? `${baseTitle} · ${modelBit}` : baseTitle
      this.historyQuery = query
      this.revealHistory()
    },
    async loadRentalcompanies() {
      try {
        const res = await this.$request(PATH.MONITORING.RENTALCOMPANY_LIST)
          .post()
          .enqueue()
        const data = res?.data || res || []
        this.rentalcompanyOptions = Array.isArray(data) ? data : []
      } catch (e) {
        console.error(e)
        this.rentalcompanyOptions = []
      }
    },
    async loadOverview() {
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_STATUS_OVERVIEW)
          .setObject(this.payload())
          .enqueue()
        const data = res?.data || res || {}
        this.overview = {
          totalReplaceCount: Number(data.totalReplaceCount || 0),
          lowEmptyPrinterCount: Number(data.lowEmptyPrinterCount || 0),
          lowEmptyItemCount: Number(
            data.lowEmptyItemCount != null
              ? data.lowEmptyItemCount
              : data.lowEmptyPrinterCount || 0
          ),
          forecastItemCount: Number(data.forecastItemCount || 0),
          errorCount: Number(data.errorCount || 0),
        }
        await this.applyDashboardListCounts()
      } catch (e) {
        console.error(e)
      }
    },
    formatForecast(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '-'
      return Math.round(n * 10) / 10
    },
    openAccountReplace(row) {
      const name = (row && (row.accountName || row.groupLabel)) || ''
      this.search.groupBy = 'ACCOUNT'
      this.search.keyword = name
      this.skuKeyword = name
      this.changeContentTab('status')
    },
    async loadAccountReplace() {
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_USAGE_LIST)
          .setObject(
            this.payload({
              groupBy: 'ACCOUNT',
              page: 1,
              rows: 15,
              orderBy: 'replaceCount',
              orderDir: 'DESC',
              keyword: null,
              trendTotalOnly: false,
            })
          )
          .enqueue()
        const data = res?.data || res || {}
        this.accountReplaceRows = Array.isArray(data.list) ? data.list : []
      } catch (e) {
        console.error(e)
        this.accountReplaceRows = []
      }
    },
    async loadUsageList() {
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_USAGE_LIST)
          .setObject(
            this.payload({
              keyword: this.search.keyword || null,
              colorCode: this.search.colorCode || null,
            })
          )
          .enqueue()
        const data = res?.data || res || {}
        this.usageList = Array.isArray(data.list) ? data.list : []
        this.totalCount = Number(data.totalCount || 0)
        this.usageLoaded = true
      } catch (e) {
        console.error(e)
        this.usageList = []
        this.totalCount = 0
        this.usageLoaded = true
      }
    },
    async loadBreakdown() {
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_USAGE_BREAKDOWN)
          .setObject(this.payload())
          .enqueue()
        const data = res?.data || res || {}
        this.categoryBreakdown = Array.isArray(data.byCategory) ? data.byCategory : []
        this.manufacturerBreakdown = Array.isArray(data.byManufacturer)
          ? data.byManufacturer
          : []
        this.breakdownLoaded = true
      } catch (e) {
        console.error(e)
        this.categoryBreakdown = []
        this.manufacturerBreakdown = []
        this.breakdownLoaded = true
      }
    },
    async loadTrend() {
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_USAGE_TREND)
          .setObject(this.payload({ trendTotalOnly: true }))
          .enqueue()
        this.trendRows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []
      } catch (e) {
        console.error(e)
        this.trendRows = []
      }
    },
    async loadForecast() {
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_PURCHASE_FORECAST)
          .setObject({ ...this.payload(), page: 1, rows: 100, groupBy: 'SUPPLY' })
          .enqueue()
        const data = res?.data !== undefined ? res.data : res
        this.forecastList = Array.isArray(data) ? data : Array.isArray(data?.list) ? data.list : []
        this.forecastLoaded = true
      } catch (e) {
        console.error(e)
        this.forecastList = []
        this.forecastLoaded = true
      }
    },
    prevPage() {
      if (this.search.page > 1) {
        this.search.page -= 1
        if (this.activeTab === 'plan') this.loadPlanList()
        else this.loadUsageList()
      }
    },
    nextPage() {
      if (this.search.page < this.totalPages) {
        this.search.page += 1
        if (this.activeTab === 'plan') this.loadPlanList()
        else this.loadUsageList()
      }
    },
    planBreakdownTip(item) {
      const urgent = Number(item && item.urgentCount ? item.urgentCount : 0).toLocaleString()
      const slots = Number(item && item.slotCount ? item.slotCount : 0).toLocaleString()
      return `${this.t('긴급')} ${urgent} · ${this.t('장착 슬롯 수')} ${slots}${this.t('개')}`
    },
    applyHorizon(days) {
      this.search.horizonDays = days
      this.reload()
    },
    toggleUnmappedOnly() {
      this.search.unmappedOnly = !this.search.unmappedOnly
      this.search.page = 1
      this.reload()
    },
    togglePlanUrgentOnly() {
      this.search.urgentOnly = !this.search.urgentOnly
      this.reloadPlanOnly()
    },
    filterPlanCategory(key) {
      this.search.supplyCategory = this.search.supplyCategory === key ? '' : key || ''
      this.reloadPlanOnly()
    },
    filterPlanManufacturer(key) {
      this.search.manufacturerName = this.search.manufacturerName === key ? '' : key || ''
      this.reloadPlanOnly()
    },
    scheduleUnmappedCount() {
      if (this.unmappedCountTimer) {
        clearTimeout(this.unmappedCountTimer)
      }
      this.unmappedCountTimer = setTimeout(() => {
        this.loadUnmappedCount()
      }, 300)
    },
    async loadUnmappedCount() {
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_STATUS_UNMAPPED)
          .setObject({
            rentalcompanyId: this.search.rentalcompanyId || null,
            excludePaperRelated: !!this.search.excludePaperRelated,
          })
          .enqueue()
        const data = res?.data || res || {}
        this.unmappedCount = Number(data.count || 0)
      } catch (e) {
        this.unmappedCount = 0
      } finally {
        this.unmappedCountLoaded = true
      }
    },
    parseStatusSummary(res) {
      const data = res?.data !== undefined ? res.data : res
      if (Array.isArray(data)) {
        const list = data
        return {
          list,
          totalCount: list.length,
          errorCount: list.reduce((sum, row) => sum + Number(row.errorCount || 0), 0),
          lowEmptyCount: list.reduce(
            (sum, row) => sum + Number(row.lowCount || 0) + Number(row.emptyCount || 0),
            0,
          ),
          urgentCount: list.reduce((sum, row) => sum + Number(row.urgentCount || 0), 0),
          slotCount: list.reduce((sum, row) => sum + Number(row.activeCount || 0), 0),
          compatibleSlotCount: 0,
          oemSlotCount: 0,
        }
      }
      const list = Array.isArray(data?.list) ? data.list : []
      return {
        list,
        totalCount: Number(data?.totalCount || list.length),
        errorCount: Number(data?.errorCount || 0),
        lowEmptyCount: Number(data?.lowEmptyCount || 0),
        urgentCount: Number(data?.urgentCount || 0),
        slotCount: Number(data?.slotCount || 0),
        compatibleSlotCount: Number(data?.compatibleSlotCount || 0),
        oemSlotCount: Number(data?.oemSlotCount || 0),
      }
    },
    async loadSkuInsight() {
      try {
        const groupBy = this.activeTab === 'status' ? this.search.groupBy : 'SUPPLY'
        const payload = this.payload({
          groupBy,
          keyword: this.skuKeyword || '',
          page: this.skuPage,
          rows: this.skuRows,
          orderBy: this.skuOrderBy,
          orderDir: this.skuOrderDir,
          unmappedOnly: false,
          requirePartNumber: groupBy === 'SUPPLY' && !this.statusIncludeUnmapped,
          lowEmptyOnly: this.statusCardLowEmpty === true,
        })
        const summaryRes = await this.$request(PATH.MONITORING.SUPPLY_STATUS_SUMMARY)
          .setObject(payload)
          .enqueue()
        const parsed = this.parseStatusSummary(summaryRes)
        this.statusSummary = parsed.list
        this.skuTotalCount = parsed.totalCount
        this.skuInsightTotals = {
          errorCount: parsed.errorCount,
          lowEmptyCount: parsed.lowEmptyCount,
          urgentCount: parsed.urgentCount,
        }
        this.skuUsageList = []
        this.skuPlanList = []
      } catch (e) {
        console.error(e)
        this.statusSummary = []
        this.skuUsageList = []
        this.skuPlanList = []
        this.skuTotalCount = 0
        this.skuInsightTotals = { errorCount: 0, lowEmptyCount: 0, urgentCount: 0 }
      }
    },
    onSkuSearch() {
      this.skuPage = 1
      this.loadSkuInsight()
    },
    onSkuSort(field) {
      if (this.skuOrderBy === field) {
        this.skuOrderDir = this.skuOrderDir === 'ASC' ? 'DESC' : 'ASC'
      } else {
        this.skuOrderBy = field
        this.skuOrderDir = 'DESC'
      }
      this.skuPage = 1
      this.loadSkuInsight()
    },
    onSkuPageChange(page) {
      const next = Number(page || 1)
      if (next < 1 || next === this.skuPage) return
      this.skuPage = next
      this.loadSkuInsight()
    },
    skuSlotDetailQuery(row, statusCodes) {
      const part = String(row?.partNumber || '').trim()
      const query = {
        rentalcompanyId: this.search.rentalcompanyId || null,
        fromDate: this.search.fromDate,
        toDate: this.search.toDate,
        excludePaperRelated: !!this.search.excludePaperRelated,
        slotDetail: true,
        lightQuery: true,
        page: 1,
        rows: 50,
      }
      if (statusCodes && statusCodes.length) {
        query.statusCodes = statusCodes
        if (statusCodes.indexOf('ERROR') >= 0) query.includeError = true
      }
      if (this.search.groupBy === 'RENTAL') {
        const rentalId = Number(row?.rentalcompanyId || row?.groupKey || this.search.rentalcompanyId || 0)
        if (rentalId > 0) {
          query.rentalcompanyId = rentalId
          return query
        }
      }
      if (this.search.groupBy === 'MODEL') {
        const modelId = Number(row?.printerModelId || row?.groupKey || 0)
        if (modelId > 0) {
          query.printerModelId = modelId
          return query
        }
      }
      if (part) {
        query.partNumberExact = part
        if (row.supplyCategory) query.supplyCategory = row.supplyCategory
        if (row.colorCode && String(row.colorCode).toUpperCase() !== 'UNKNOWN') {
          query.colorCode = row.colorCode
        }
        return query
      }
      const snapKey = String(row.marketItemKey || row.groupKey || '')
      if (snapKey) query.groupKey = snapKey
      return query
    },
    onSkuDetail({ row, kind }) {
      if (!row) return
      if (kind === 'plan' || kind === 'next') {
        this.openPlanDrawer(row, { urgentOnly: false })
        return
      }
      if (kind === 'urgent') {
        this.historyDrawerMode = 'usage'
        this.historyDrawerTitle = `${this.formatSkuInsightLabel(row)} · ${this.t('긴급')}`
        const query = this.skuSlotDetailQuery(row, null)
        query.urgentOnly = true
        query.includeError = true
        this.historyQuery = query
        this.revealHistory()
        return
      }
      if (kind === 'forecast') {
        this.openPlanDrawer(row, { urgentOnly: false })
        return
      }
      if (kind === 'printer') {
        this.historyDrawerMode = 'usage'
        this.historyDrawerTitle = `${this.formatSkuInsightLabel(row)} · ${this.t('프린터')}`
        const query = this.skuSlotDetailQuery(row, null)
        query.includeError = true
        query.distinctPrinter = true
        this.historyQuery = query
        this.revealHistory()
        return
      }
      if (kind === 'slot') {
        this.historyDrawerMode = 'usage'
        this.historyDrawerTitle = `${this.formatSkuInsightLabel(row)} · ${this.t('장착 슬롯')}`
        const query = this.skuSlotDetailQuery(row, null)
        query.includeError = true
        this.historyQuery = query
        this.revealHistory()
        return
      }
      if (kind === 'usage' || kind === 'remain') {
        this.historyDrawerMode = 'usage'
        this.historyDrawerTitle = `${this.formatSkuInsightLabel(row)} · ${this.t('사용 목록')}`
        const query = this.skuSlotDetailQuery(row, null)
        query.includeError = true
        this.historyQuery = query
        this.revealHistory()
        return
      }
      if (kind === 'lowEmpty' || kind === 'error') {
        this.historyDrawerMode = kind === 'error' ? 'error' : 'lowEmpty'
        this.historyDrawerTitle = `${this.formatSkuInsightLabel(row)} · ${
          kind === 'error' ? this.t('에러') : this.t('LOW/EMPTY')
        }`
        this.historyQuery = this.skuSlotDetailQuery(
          row,
          kind === 'error' ? ['ERROR'] : ['LOW', 'EMPTY'],
        )
        this.revealHistory()
        return
      }
      this.openSupplyDetail(row)
    },
    skuGroupCountLabel() {
      if (this.search.groupBy === 'MODEL') return this.t('모델 수')
      if (this.search.groupBy === 'ACCOUNT') return this.t('거래처 수')
      if (this.search.groupBy === 'RENTAL') return this.t('임대업체 수')
      return this.t('SKU 수')
    },
    onSkuKpi(kind) {
      if (kind === 'sku') {
        const groupBy = this.search.groupBy || 'SUPPLY'
        this.historyDrawerMode = 'sku'
        this.historyDrawerTitle = `${this.skuGroupCountLabel()} (${Number(this.skuTotalCount || 0).toLocaleString()})`
        this.historyQuery = {
          rentalcompanyId: this.search.rentalcompanyId || null,
          excludePaperRelated: !!this.search.excludePaperRelated,
          keyword: this.skuKeyword || '',
          groupBy,
          requirePartNumber: groupBy === 'SUPPLY' && !this.statusIncludeUnmapped,
        }
        this.revealHistory()
        return
      }
      const extra = {
        includeError: true,
        requirePartNumber: !this.statusIncludeUnmapped,
        slotDetail: true,
        lightQuery: true,
        page: 1,
        rows: 50,
      }
      if (kind === 'error') extra.statusCodes = ['ERROR']
      if (kind === 'lowEmpty') extra.statusCodes = ['LOW', 'EMPTY']
      if (kind === 'urgent') extra.urgentOnly = true
      const count =
        kind === 'error'
          ? this.skuInsightTotals.errorCount
          : kind === 'lowEmpty'
            ? this.skuInsightTotals.lowEmptyCount
            : this.skuInsightTotals.urgentCount
      this.historyDrawerMode = kind === 'urgent' ? 'usage' : kind
      this.historyDrawerTitle =
        kind === 'error'
          ? `${this.t('에러 슬롯')} (${Number(count || 0).toLocaleString()})`
          : kind === 'lowEmpty'
            ? `${this.t('LOW/EMPTY')} (${Number(count || 0).toLocaleString()})`
            : `${this.t('긴급 수량')} (${Number(count || 0).toLocaleString()})`
      this.historyQuery = {
        rentalcompanyId: this.search.rentalcompanyId || null,
        excludePaperRelated: !!this.search.excludePaperRelated,
        keyword: this.skuKeyword || '',
        ...extra,
      }
      this.revealHistory()
    },
    currentRemainBucketLabel() {
      const map = {
        R0: '0%',
        R1_20: '1–20%',
        R21_50: '21–50%',
        R51_80: '51–80%',
        R81_100: '81–100%',
        UNKNOWN: this.t('잔량없음'),
      }
      return map[this.currentRemainBucket] || ''
    },
    formatSkuInsightLabel(row) {
      const part = String(row?.partNumber || '').trim()
      if (part) return part
      return this.formatGroupLabel(row)
    },
    async loadCurrentAsset() {
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_STATUS_ASSET)
          .setObject(
            this.payload({
              unmappedOnly: false,
              supplyCategory: null,
              keyword: null,
              partNumber: null,
              colorCode: null,
              manufacturerName: null,
            }),
          )
          .enqueue()
        const data = res?.data || res || {}
        this.currentAsset = {
          totals: data.totals || {},
          byCategory: Array.isArray(data.byCategory) ? data.byCategory : [],
          byManufacturer: Array.isArray(data.byManufacturer) ? data.byManufacturer : [],
          byStatus: Array.isArray(data.byStatus) ? data.byStatus : [],
          byRemain: Array.isArray(data.byRemain) ? data.byRemain : [],
        }
      } catch (e) {
        console.error(e)
        this.currentAsset = {
          totals: {},
          byCategory: [],
          byManufacturer: [],
          byStatus: [],
          byRemain: [],
        }
      }
    },
    async loadCurrentStatus() {
      try {
        const remainMax = String(this.currentRemainMax || '').trim()
        const remainBucket = this.currentRemainBucket || ''
        const res = await this.$request(PATH.MONITORING.SUPPLY_STATUS_SUMMARY)
          .setObject(
            this.payload({
              groupBy: this.search.groupBy,
              keyword: this.currentKeyword || this.salesKeyword || this.search.keyword || '',
              colorCode: this.currentColorCode || '',
              statusCode: this.currentStatusCode || '',
              remainMax: remainBucket || remainMax === '' ? null : Number(remainMax),
              remainBucket: remainBucket || null,
              supplyCategory: this.currentCategory || this.search.supplyCategory || null,
              manufacturerName: this.currentManufacturer || this.search.manufacturerName || null,
              unmappedOnly: false,
              page: this.currentPage,
              rows: this.currentRowsPerPage,
              orderBy: this.currentOrderBy,
              orderDir: this.currentOrderDir,
            }),
          )
          .enqueue()
        const parsed = this.parseStatusSummary(res)
        this.currentRows = parsed.list
        this.currentTotalCount = parsed.totalCount
        await this.enrichCurrentPlanBridge(parsed.list)
      } catch (e) {
        console.error(e)
        this.currentRows = []
        this.currentTotalCount = 0
      }
    },
    async enrichCurrentPlanBridge(rows) {
      const partNumbers = (rows || [])
        .map((row) => String(row.partNumber || '').trim())
        .filter(Boolean)
      if (!partNumbers.length && this.search.groupBy === 'SUPPLY') return
      try {
        const planRes = await this.$request(PATH.MONITORING.SUPPLY_REPLACE_PLAN_LIST)
          .setObject(
            this.payload({
              groupBy: this.search.groupBy,
              page: 1,
              rows: Math.max(rows.length, 1),
              partNumbers: this.search.groupBy === 'SUPPLY' ? partNumbers : undefined,
              unmappedOnly: false,
            }),
          )
          .enqueue()
        const planData = planRes?.data || planRes || {}
        const planList = Array.isArray(planData.list) ? planData.list : []
        const planByKey = {}
        planList.forEach((row) => {
          const key = this.skuMergeKey(row)
          if (key && !planByKey[key]) planByKey[key] = row
        })
        this.currentRows = (this.currentRows || []).map((row) => {
          const plan = planByKey[this.skuMergeKey(row)] || {}
          return {
            ...row,
            urgentCount: plan.urgentCount != null ? plan.urgentCount : row.urgentCount,
            nextReplaceDate: plan.nextReplaceDate || row.nextReplaceDate,
          }
        })
      } catch (e) {
        console.error(e)
      }
    },
    onCurrentUrgent(row) {
      if (!row) return
      this.openPlanDrawer(row, { urgentOnly: Number(row.urgentCount || 0) > 0 })
    },
    onCurrentSearch() {
      this.currentPage = 1
      this.currentRemainBucket = ''
      this.loadCurrentStatus()
    },
    onCurrentSort(field) {
      if (this.currentOrderBy === field) {
        this.currentOrderDir = this.currentOrderDir === 'ASC' ? 'DESC' : 'ASC'
      } else {
        this.currentOrderBy = field
        this.currentOrderDir = 'DESC'
      }
      this.currentPage = 1
      this.loadCurrentStatus()
    },
    onCurrentPageChange(page) {
      const next = Number(page || 1)
      if (next < 1 || next === this.currentPage) return
      this.currentPage = next
      this.loadCurrentStatus()
    },
    onCurrentKpi(kind) {
      if (kind !== 'all' && kind !== 'printer') return
      this.currentCategory = ''
      this.currentManufacturer = ''
      this.currentStatusCode = ''
      this.currentRemainBucket = ''
      this.currentRemainMax = ''
      this.currentPage = 1
      this.loadCurrentStatus()
    },
    onCurrentFilterCategory(key) {
      this.currentCategory = this.currentCategory === key ? '' : key || ''
      this.currentPage = 1
      this.loadCurrentStatus()
    },
    onCurrentFilterManufacturer(key) {
      this.currentManufacturer = this.currentManufacturer === key ? '' : key || ''
      this.currentPage = 1
      this.loadCurrentStatus()
    },
    onCurrentFilterStatus(key) {
      this.currentStatusCode = this.currentStatusCode === key ? '' : key || ''
      this.currentPage = 1
      this.loadCurrentStatus()
    },
    onCurrentFilterRemain(key) {
      this.currentRemainBucket = this.currentRemainBucket === key ? '' : key || ''
      this.currentRemainMax = ''
      this.currentPage = 1
      this.loadCurrentStatus()
    },
    openCurrentUsage(row) {
      if (!row) return
      this.historyDrawerMode = 'usage'
      const remainHint = this.currentRemainBucketLabel()
      this.historyDrawerTitle = remainHint
        ? `${this.formatSkuInsightLabel(row)} · ${remainHint} · ${this.t('사용 목록')}`
        : `${this.formatSkuInsightLabel(row)} · ${this.t('사용 목록')}`
      const query = this.skuSlotDetailQuery(row, null)
      query.includeError = true
      if (this.currentStatusCode) query.statusCode = this.currentStatusCode
      if (this.currentManufacturer) query.manufacturerName = this.currentManufacturer
      if (this.currentRemainBucket) query.remainBucket = this.currentRemainBucket
      const remainMax = String(this.currentRemainMax || '').trim()
      if (!this.currentRemainBucket && remainMax !== '') query.remainMax = Number(remainMax)
      this.historyQuery = query
      this.revealHistory()
    },
    reloadPlanOnly() {
      this.search.page = 1
      this.loadPlan()
    },
    sortPlanBy(field) {
      if (this.search.orderBy === field) {
        this.search.orderDir = this.search.orderDir === 'ASC' ? 'DESC' : 'ASC'
      } else {
        this.search.orderBy = field
        this.search.orderDir = 'DESC'
      }
      this.search.page = 1
      this.loadPlanList()
    },
    async loadPlan() {
      await Promise.all([this.loadPlanOverview(), this.loadPlanList()])
    },
    async loadPlanOverview() {
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_REPLACE_PLAN_OVERVIEW)
          .setObject(this.payload())
          .enqueue()
        const data = res?.data || res || {}
        this.planOverview = {
          urgentCount: Number(data.urgentCount || 0),
          qty30: Number(data.qty30 || 0),
          qty60: Number(data.qty60 || 0),
          qty90: Number(data.qty90 || 0),
          qty180: Number(data.qty180 || 0),
          qtyHorizon: Number(data.qtyHorizon || 0),
          skuCount: Number(data.skuCount || 0),
          predictedSlotCount: Number(data.predictedSlotCount || 0),
          insufficientCount: Number(data.insufficientCount || 0),
          byCategory: Array.isArray(data.byCategory) ? data.byCategory : [],
          byManufacturer: Array.isArray(data.byManufacturer) ? data.byManufacturer : [],
        }
      } catch (e) {
        console.error(e)
      }
    },
    async loadPlanList() {
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_REPLACE_PLAN_LIST)
          .setObject(this.payload())
          .enqueue()
        const data = res?.data || res || {}
        this.planList = Array.isArray(data.list) ? data.list : []
        this.planTotalCount = Number(data.totalCount || 0)
        this.planLoaded = true
      } catch (e) {
        console.error(e)
        this.planList = []
        this.planTotalCount = 0
        this.planLoaded = true
      }
    },
    planBaseQuery() {
      return {
        rentalcompanyId: this.search.rentalcompanyId || null,
        supplyCategory: this.search.supplyCategory || null,
        partNumber: this.search.partNumber || null,
        manufacturerName: this.search.manufacturerName || null,
        keyword: this.search.keyword || null,
        colorCode: this.search.colorCode || null,
        excludePaperRelated: !!this.search.excludePaperRelated,
        unmappedOnly: !!this.search.unmappedOnly,
        cycleLookbackDays: this.search.cycleLookbackDays,
        horizonDays: this.search.horizonDays,
      }
    },
    openPlanDrawerUrgent() {
      this.historyDrawerMode = 'usage'
      this.historyDrawerTitle = `${this.t('긴급 교체 필요')} (${Number(
        this.planOverview.urgentCount || 0,
      ).toLocaleString()})`
      this.historyQuery = {
        ...this.planBaseQuery(),
        urgentOnly: true,
        slotDetail: true,
        page: 1,
        rows: 50,
      }
      this.revealHistory()
    },
    openPlanDrawer(row, options = {}) {
      const urgentOnly = options.urgentOnly != null ? !!options.urgentOnly : !!this.search.urgentOnly
      this.historyDrawerMode = 'usage'
      this.historyDrawerTitle = `${this.formatPlanRowLabel(row)} · ${this.t('슬롯 목록')}`
      const query = {
        ...this.planBaseQuery(),
        urgentOnly,
        slotDetail: true,
        lightQuery: this.activeTab === 'status',
        page: 1,
        rows: 50,
      }
      if (this.search.groupBy === 'RENTAL' && row.rentalcompanyId) {
        query.rentalcompanyId = row.rentalcompanyId
      } else if (this.search.groupBy === 'MODEL' && row.printerModelId) {
        query.printerModelId = row.printerModelId
      } else if (this.search.groupBy === 'ACCOUNT' && row.accountId) {
        query.accountId = row.accountId
      }
      if (this.activeTab === 'plan' && (row.marketItemKey || this.search.groupBy === 'SUPPLY')) {
        query.groupKey = row.marketItemKey || row.groupKey || ''
      } else if (!query.printerModelId && !query.accountId && !query.rentalcompanyId) {
        query.groupKey = row.groupKey || row.marketItemKey || ''
      }
      this.historyQuery = query
      this.revealHistory()
    },
    exportForecastCsv() {
      if (!this.forecastList.length) {
        this.$toasted?.show?.(this.t('내보낼 데이터가 없습니다.'))
        return
      }
      const header = [
        'marketItemKey',
        'groupKey',
        'identityType',
        'manufacturerName',
        'printerModelName',
        'partNumber',
        'description',
        'supplyLabel',
        'supplyCategory',
        'colorCode',
        'monthlyForecast',
        'rentalCompanyCount',
        'printerCount',
        'replaceCount',
      ]
      this.downloadCsv(
        header,
        this.forecastList,
        `supply-purchase-forecast-${this.search.fromDate}_${this.search.toDate}.csv`,
      )
    },
    downloadCsv(header, rows, filename) {
      const lines = [header.join(',')]
      rows.forEach((row) => {
        lines.push(
          header
            .map((k) => {
              const v = row[k] == null ? '' : String(row[k]).replace(/"/g, '""')
              return `"${v}"`
            })
            .join(','),
        )
      })
      const blob = new Blob(['\uFEFF' + lines.join('\n')], {
        type: 'text/csv;charset=utf-8;',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    },
    salesPayload(extra = {}) {
      const unmappedOnly = this.salesDrill
        ? !!this.search.unmappedOnly
        : this.salesSearchType === 'DESCRIPTION'
          ? true
          : !!this.search.unmappedOnly
      return this.payload({
        keyword: this.salesKeyword || null,
        searchType: this.salesSearchType || 'AUTO',
        unmappedOnly,
        partNumberExact: this.search.partNumberExact || null,
        printerModelId: this.search.printerModelId || null,
        groupKey: this.search.groupKey || null,
        manufacturerName: this.search.manufacturerName || null,
        ...extra,
      })
    },
    async loadSales() {
      await Promise.all([this.loadSalesKpis(), this.loadSalesList()])
    },
    async loadSalesKpis() {
      try {
        const base = this.salesPayload({ page: 1, rows: 1, groupBy: this.search.groupBy })
        const [overviewRes, summaryRes, planRes] = await Promise.all([
          this.$request(PATH.MONITORING.SUPPLY_STATUS_OVERVIEW).setObject(base).enqueue(),
          this.$request(PATH.MONITORING.SUPPLY_STATUS_SUMMARY).setObject(base).enqueue(),
          this.$request(PATH.MONITORING.SUPPLY_REPLACE_PLAN_OVERVIEW).setObject(base).enqueue(),
        ])
        const overview = overviewRes?.data || overviewRes || {}
        const summary = this.parseStatusSummary(summaryRes)
        const plan = planRes?.data || planRes || {}
        const slotCount = Number(summary.slotCount || 0)
        const compatible = Number(summary.compatibleSlotCount || 0)
        const oem = Number(summary.oemSlotCount || 0)
        const known = compatible + oem
        this.salesKpis = {
          replaceCount: Number(overview.totalReplaceCount || 0),
          lowEmptyCount: Number(
            overview.lowEmptyItemCount != null
              ? overview.lowEmptyItemCount
              : summary.lowEmptyCount || 0,
          ),
          errorCount: Number(summary.errorCount || 0),
          urgentCount: Number(plan.urgentCount || 0),
          slotCount,
          compatibleSlotCount: compatible,
          oemSlotCount: oem,
          compatRatio: known > 0 ? Math.round((compatible / known) * 100) : null,
        }
      } catch (e) {
        console.error(e)
      }
    },
    async loadSalesList() {
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_USAGE_LIST)
          .setObject(this.salesPayload())
          .enqueue()
        const data = res?.data || res || {}
        const list = Array.isArray(data.list) ? data.list : []
        this.salesTotalCount = Number(data.totalCount || 0)
        this.salesRows = await this.enrichSalesRows(list)
        this.salesLoaded = true
      } catch (e) {
        console.error(e)
        this.salesRows = []
        this.salesTotalCount = 0
        this.salesLoaded = true
      }
    },
    async enrichSalesRows(list) {
      if (this.search.groupBy !== 'SUPPLY' || !list.length) return list
      const partNumbers = list
        .map((row) => String(row.partNumber || '').trim())
        .filter(Boolean)
      if (!partNumbers.length) return list
      try {
        const summaryRes = await this.$request(PATH.MONITORING.SUPPLY_STATUS_SUMMARY)
          .setObject(
            this.salesPayload({
              page: 1,
              rows: Math.min(Math.max(partNumbers.length, 1), 200),
              partNumbers,
              keyword: null,
              groupBy: 'SUPPLY',
            }),
          )
          .enqueue()
        const parsed = this.parseStatusSummary(summaryRes)
        const byPart = {}
        parsed.list.forEach((row) => {
          const key = String(row.partNumber || '').trim()
          if (!key) return
          if (!byPart[key]) {
            byPart[key] = {
              lowEmptyCount: 0,
              errorCount: 0,
              slotCount: 0,
              compatibleCount: 0,
            }
          }
          const slot = Number(row.activeCount || 0)
          byPart[key].lowEmptyCount += Number(row.lowCount || 0) + Number(row.emptyCount || 0)
          byPart[key].errorCount += Number(row.errorCount || 0)
          byPart[key].slotCount += slot
          const compat = String(row.compatibilityType || '').toUpperCase()
          if (compat === 'COMPATIBLE' || compat === 'REMANUFACTURED') {
            byPart[key].compatibleCount += slot
          }
        })
        return list.map((row) => {
          const extra = byPart[String(row.partNumber || '').trim()] || {}
          return { ...row, ...extra }
        })
      } catch (e) {
        console.error(e)
        return list
      }
    },
    onSalesSearch() {
      this.search.page = 1
      this.search.keyword = this.salesKeyword
      this.search.searchType = this.salesSearchType
      if (!this.salesDrill) {
        this.search.manufacturerName = ''
        this.search.partNumberExact = ''
        this.search.printerModelId = null
        this.search.groupKey = ''
      }
      this.applySalesLanding()
      this.loadSales()
    },
    applySalesLanding() {
      if (this.salesDrill) return
      if (this.salesSearchType === 'MANUFACTURER' || this.salesSearchType === 'MODEL') {
        this.search.groupBy = 'MODEL'
      } else if (this.salesSearchType === 'PART' || this.salesSearchType === 'DESCRIPTION') {
        this.search.groupBy = 'SUPPLY'
      }
    },
    onSalesSuggest(value) {
      if (this.salesSuggestTimer) clearTimeout(this.salesSuggestTimer)
      const q = String(value || '').trim()
      if (!q) {
        this.salesSuggestions = []
        return
      }
      this.salesSuggestTimer = setTimeout(() => {
        this.fetchSalesSuggest(q)
      }, 250)
    },
    async fetchSalesSuggest(q) {
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_SUGGEST)
          .setObject(
            this.salesPayload({
              keyword: q,
              rows: 8,
              page: 1,
            }),
          )
          .enqueue()
        const data = res?.data || res || {}
        this.salesSuggestions = Array.isArray(data.list) ? data.list : []
      } catch (e) {
        this.salesSuggestions = []
      }
    },
    onSalesPick(item) {
      if (!item) return
      this.salesSuggestions = []
      this.salesSearchType = item.type || this.salesSearchType
      this.salesKeyword = item.value || item.label || ''
      this.search.page = 1
      this.clearSalesDrillFields()
      if (item.type === 'MANUFACTURER') {
        this.search.groupBy = 'MODEL'
        this.search.manufacturerName = item.value
        this.salesKeyword = item.value
      } else if (item.type === 'MODEL') {
        this.search.groupBy = 'MODEL'
        this.search.manufacturerName = item.manufacturerName || this.search.manufacturerName || ''
      } else if (item.type === 'PART') {
        this.search.groupBy = 'SUPPLY'
        this.search.partNumberExact = item.value
      } else if (item.type === 'DESCRIPTION') {
        this.search.groupBy = 'SUPPLY'
      }
      this.search.searchType = this.salesSearchType
      this.loadSales()
    },
    clearSalesSearch() {
      this.salesKeyword = ''
      this.salesSearchType = 'AUTO'
      this.salesSuggestions = []
      this.search.searchType = 'AUTO'
      this.search.manufacturerName = ''
      this.search.partNumberExact = ''
      this.search.printerModelId = null
      this.search.groupKey = ''
      this.search.keyword = ''
      this.search.groupBy = 'SUPPLY'
      this.salesDrill = null
      this.search.page = 1
      this.loadSales()
    },
    clearSalesDrillFields() {
      this.search.partNumberExact = ''
      this.search.printerModelId = null
      this.search.groupKey = ''
    },
    onSalesChangeGroup(groupBy) {
      this.search.groupBy = groupBy
      this.search.page = 1
      this.search.orderBy = 'replaceCount'
      this.search.orderDir = 'DESC'
      this.loadSalesList()
    },
    onSalesRowClick(row) {
      if (!row) return
      if (this.search.groupBy === 'SUPPLY') {
        this.salesDrill = {
          kind: 'SKU',
          label: row.partNumber || row.groupLabel || '-',
        }
        this.search.groupBy = 'RENTAL'
        this.search.groupKey = row.groupKey || row.marketItemKey || ''
        this.search.partNumberExact = row.partNumber || ''
        this.search.page = 1
        this.loadSales()
        return
      }
      if (this.search.groupBy === 'MODEL') {
        this.salesDrill = {
          kind: 'MODEL',
          label: row.printerModelName || row.groupLabel || '-',
        }
        this.search.groupBy = 'SUPPLY'
        this.search.printerModelId = row.printerModelId || null
        this.search.manufacturerName = row.manufacturerName || this.search.manufacturerName || ''
        this.search.page = 1
        this.loadSales()
        return
      }
      if (this.search.groupBy === 'RENTAL') {
        this.salesDrill = {
          kind: 'RENTAL',
          label: row.rentalcompanyName || row.groupLabel || '-',
          prevRentalcompanyId: this.search.rentalcompanyId,
        }
        this.search.groupBy = 'SUPPLY'
        this.search.rentalcompanyId = row.rentalcompanyId || this.search.rentalcompanyId
        this.search.page = 1
        this.loadSales()
      }
    },
    clearSalesDrill() {
      const prev = this.salesDrill && this.salesDrill.prevRentalcompanyId
      if (this.salesDrill && this.salesDrill.kind === 'RENTAL') {
        this.search.rentalcompanyId = prev || null
      }
      this.salesDrill = null
      this.search.groupKey = ''
      this.search.partNumberExact = ''
      this.search.printerModelId = null
      this.search.groupBy = this.salesSearchType === 'MANUFACTURER' || this.salesSearchType === 'MODEL'
        ? 'MODEL'
        : 'SUPPLY'
      this.search.page = 1
      this.loadSales()
    },
    onSalesPageChange(page) {
      const next = Number(page || 1)
      if (next < 1 || next === this.search.page) return
      this.search.page = next
      this.loadSalesList()
    },
    onSalesKpi(kind) {
      if (kind === 'urgent') {
        this.openPlanDrawerUrgent()
        return
      }
      this.historyDrawerMode =
        kind === 'lowEmpty' ? 'lowEmpty' : kind === 'error' ? 'error' : 'history'
      this.historyDrawerTitle =
        kind === 'lowEmpty'
          ? this.t('LOW/EMPTY')
          : kind === 'error'
            ? this.t('에러 슬롯')
            : this.t('기간 총 교체 내역')
      this.historyQuery = {
        rentalcompanyId: this.search.rentalcompanyId || null,
        fromDate: this.search.fromDate,
        toDate: this.search.toDate,
        excludePaperRelated: !!this.search.excludePaperRelated,
        keyword: this.salesKeyword || '',
        searchType: this.salesSearchType,
        manufacturerName: this.search.manufacturerName || null,
        partNumberExact: this.search.partNumberExact || null,
        printerModelId: this.search.printerModelId || null,
        groupKey: this.search.groupKey || null,
      }
      if (kind === 'error') {
        this.historyQuery.statusCodes = ['ERROR']
        this.historyQuery.includeError = true
      }
      if (kind === 'lowEmpty') this.historyQuery.statusCodes = ['LOW', 'EMPTY']
      this.revealHistory()
    },
    openSalesSkuInsight(row) {
      this.statusView = 'compare'
      this.skuKeyword = row.partNumber || this.salesKeyword || ''
      this.changeContentTab('sku')
    },
    onSkuRentalRank(row) {
      this.activeTab = 'sales'
      this.salesSearchType = 'PART'
      this.salesKeyword = row.partNumber || ''
      this.search.searchType = 'PART'
      this.search.groupBy = 'RENTAL'
      this.search.groupKey = row.groupKey || row.marketItemKey || ''
      this.search.partNumberExact = row.partNumber || ''
      this.salesDrill = {
        kind: 'SKU',
        label: row.partNumber || row.groupLabel || '-',
      }
      this.search.page = 1
      this.loadSales()
    },
    openCommonPartMap(row) {
      this.commonPartContext = {
        printerModelId: row.printerModelId || 0,
        printerModelName: row.printerModelName || '',
        manufacturerName: row.manufacturerName || '',
        supplyCategory: row.supplyCategory || 'OTHER',
        colorCode: row.colorCode || 'UNKNOWN',
        description: row.description || row.descriptionNorm || '',
        partNumber: row.partNumber || '',
        slotCount: row.slotCount || row.activeCount || row.printerCount || 0,
      }
      this.commonPartOpen = true
    },
    onCommonPartSaved() {
      this.commonPartRefreshKey += 1
      if (this.activeTab === 'common-part') return
      this.reload()
    },
    async exportUsageCsv() {
      await this.exportAxisCsv(this.payload({ page: 1, rows: 200 }), 'usage')
    },
    async exportSalesCsv() {
      await this.exportAxisCsv(this.salesPayload({ page: 1, rows: 200 }), 'sales')
    },
    async exportAxisCsv(body, prefix) {
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_USAGE_LIST)
          .setObject(body)
          .enqueue()
        const data = res?.data || res || {}
        const list = Array.isArray(data.list) ? data.list : []
        if (!list.length) {
          this.$toasted?.show?.(this.t('내보낼 데이터가 없습니다.'))
          return
        }
        const axis = body.groupBy || this.search.groupBy || 'SUPPLY'
        const header =
          axis === 'RENTAL'
            ? [
                'rentalcompanyId',
                'rentalcompanyName',
                'replaceCount',
                'printerCount',
                'monthlyForecast',
                'lastReplacedDatetime',
              ]
            : axis === 'MODEL'
              ? [
                  'printerModelId',
                  'manufacturerName',
                  'printerModelName',
                  'replaceCount',
                  'printerCount',
                  'rentalCompanyCount',
                  'monthlyForecast',
                  'lastReplacedDatetime',
                ]
              : [
                  'groupKey',
                  'identityType',
                  'manufacturerName',
                  'printerModelName',
                  'partNumber',
                  'description',
                  'supplyCategory',
                  'colorCode',
                  'replaceCount',
                  'printerCount',
                  'rentalCompanyCount',
                  'monthlyForecast',
                  'lastReplacedDatetime',
                ]
        this.downloadCsv(
          header,
          list,
          `supply-${prefix}-${axis.toLowerCase()}-${this.search.fromDate}_${this.search.toDate}.csv`,
        )
      } catch (e) {
        console.error(e)
      }
    },
  },
}
</script>

<style scoped>
.query-box {
  align-items: center;
}
.query-box-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin-right: 4px;
}
.supply-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 8px 0;
}
.preset-group {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.preset-btn {
  height: 30px;
  padding: 0 8px;
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}
.preset-btn.on {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}
.toggle-btn {
  height: 34px;
  padding: 0 10px;
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}
.toggle-btn.on {
  border-color: #0f766e;
  color: #0f766e;
  background: #ecfdf5;
}
.metric-with-compare {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.compare-badge {
  font-style: normal;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 999px;
  line-height: 1.4;
}
.compare-badge.compare-higher {
  background: #fee2e2;
  color: #b91c1c;
}
.compare-badge.compare-lower {
  background: #dcfce7;
  color: #15803d;
}
.compare-badge.compare-similar {
  background: #e2e8f0;
  color: #475569;
}
.toggle-state {
  margin-left: 4px;
  font-weight: 700;
}
.supply-control {
  height: 34px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0 8px;
  min-width: 120px;
}
.supply-btn {
  height: 34px;
  padding: 0 12px;
  border: 1px solid #2563eb;
  background: #2563eb;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
}
.supply-btn.outline {
  background: #fff;
  color: #2563eb;
}
.supply-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.supply-body {
  width: 100%;
  padding: 8px 4px 24px;
  height: 100%;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
}
.content-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 8px;
}
.content-tabs button {
  border: 1px solid #cbd5e1;
  background: #fff;
  padding: 8px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
}
.content-tabs button.active {
  background: #0f172a;
  border-color: #0f172a;
  color: #fff;
}
.dash-row {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  margin: 0 0 14px;
}
.part-card {
  box-sizing: border-box;
  width: 100%;
  margin: 0 0 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
}
.part-card--full {
  width: 100%;
}
.supply-account-basis {
  color: #64748b;
  font-size: 12px;
}
.supply-account-table-wrap {
  overflow-x: auto;
}
.supply-account-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.supply-account-table th,
.supply-account-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #eef2f7;
  text-align: left;
}
.supply-account-table th.num,
.supply-account-table td.num {
  text-align: right;
  white-space: nowrap;
}
.supply-account-link {
  border: 0;
  background: transparent;
  color: #0c98fe;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
  text-align: left;
}
.breakdown-pair-wrap {
  container-type: inline-size;
  container-name: supply-breakdown;
  width: 100%;
  margin: 0 0 14px;
}
.breakdown-pair {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
}
.breakdown-panel {
  flex: 1 1 0;
  width: auto;
  min-width: 0;
  margin: 0;
}
@container supply-breakdown (max-width: 880px) {
  .breakdown-pair {
    flex-direction: column;
    align-items: stretch;
  }
}
.part-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.part-card-head h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}
.part-card-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.part-card-actions .supply-btn {
  height: 28px;
  padding: 0 8px;
  font-size: 12px;
}
.part-card-actions .supply-btn.on {
  background: #eff6ff;
  border-color: #2563eb;
}
.part-card-tools {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}
.part-card-tools .supply-control {
  margin-right: 8px;
}
.item-card-grid {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
}
.item-card {
  box-sizing: border-box;
  width: 160px;
  margin: 0 10px 10px 0;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
}
.kpi-grid .item-card {
  width: calc(33.33% - 10px);
}
.item-card.clickable {
  cursor: pointer;
}
.item-card.clickable:hover {
  border-color: #2563eb;
}
.item-card.is-active {
  border-color: #2563eb;
  background: #eff6ff;
}
.item-card .label {
  color: #64748b;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.item-card .value {
  margin-top: 6px;
  font-size: 24px;
  font-weight: 700;
}
.item-card .meta {
  margin-top: 6px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.4;
}
.ghost-sort {
  height: 34px;
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 4px;
  padding: 0 10px;
  cursor: pointer;
  font-size: 12px;
  color: #334155;
}
.status-view-tabs {
  margin: 0 0 12px;
  justify-content: flex-start;
}
.supply-tabs {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
}
.supply-tabs button {
  border: 1px solid #cbd5e1;
  background: #fff;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
}
.supply-tabs button.active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}
.supply-tabs .ml {
  margin-left: auto;
}
.supply-panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 14px;
  margin-bottom: 14px;
}
.supply-table {
  width: 100%;
  border-collapse: collapse;
}
.supply-table th,
.supply-table td {
  border-bottom: 1px solid #e2e8f0;
  padding: 8px;
  text-align: left;
  font-size: 13px;
  vertical-align: top;
}
.supply-table th.sortable {
  cursor: pointer;
  user-select: none;
}
.supply-table th.sortable:hover {
  color: #2563eb;
}
.desc-cell,
.info-cell {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nowrap {
  white-space: nowrap;
}
.clickable-row {
  cursor: pointer;
}
.clickable-row:hover {
  background: #f1f5f9;
}
.trend-bars {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  min-height: 160px;
  overflow-x: auto;
  padding-top: 8px;
}
.trend-bar-item {
  min-width: 48px;
  text-align: center;
}
.trend-bar-item .bar {
  width: 24px;
  margin: 0 auto;
  background: #38bdf8;
  border-radius: 3px 3px 0 0;
}
.trend-bar-item .bar-label {
  font-size: 10px;
  color: #64748b;
  margin-top: 4px;
}
.trend-bar-item .bar-value {
  font-size: 11px;
  font-weight: 600;
}
.empty {
  text-align: center;
  color: #94a3b8;
  padding: 16px;
}
.pager {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}
.data-quality-notice {
  margin: 0 0 8px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #fde68a;
  background: #fffbeb;
  color: #92400e;
  font-size: 12px;
  line-height: 1.45;
}
.hint {
  color: #64748b;
  font-size: 12px;
  margin: 0 0 8px;
}
.breakdown-block {
  margin-bottom: 14px;
}
.breakdown-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.panel-head h3 {
  margin: 0;
}
.list-tools {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.search-wide {
  min-width: 240px;
}
.breakdown-table-wrap {
  max-height: 240px;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}
.supply-table.compact th,
.supply-table.compact td {
  padding: 6px 8px;
}
.supply-table .num {
  text-align: right;
  white-space: nowrap;
}
.clickable-row.is-active {
  background: #eff6ff;
}
.breakdown-head h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}
.breakdown-total {
  font-size: 12px;
  color: #64748b;
}
.breakdown-cards {
  flex-wrap: wrap;
  margin-bottom: 0;
}
.breakdown-cards .supply-card {
  flex: 1 1 140px;
  min-width: 120px;
  max-width: 220px;
}
.supply-card--toner .value {
  color: #2563eb;
}
.supply-card--drum .value {
  color: #7c3aed;
}
.supply-card--waste .value {
  color: #ea580c;
}
.supply-card--other .value {
  color: #64748b;
}
.toolbar-label {
  font-size: 12px;
  color: #475569;
  white-space: nowrap;
}
.dash-row {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  margin: 0 0 14px;
}
.part-card {
  box-sizing: border-box;
  width: 100%;
  margin: 0 0 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
}
.part-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.part-card-head h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}
.item-card-grid {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
}
.item-card {
  box-sizing: border-box;
  width: calc(33.33% - 10px);
  margin: 0 10px 10px 0;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
}
.item-card.clickable {
  cursor: pointer;
}
.item-card.clickable:hover {
  border-color: #2563eb;
}
.item-card .label {
  color: #64748b;
  font-size: 13px;
}
.item-card .value {
  margin-top: 6px;
  font-size: 24px;
  font-weight: 700;
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}
.panel-head h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
}
.list-tools {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.search-wide {
  min-width: 240px;
}
.horizon-col {
  background: #eff6ff;
}
.paging {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
  font-size: 13px;
  color: #475569;
}
.map-link-btn {
  border: 1px solid #93c5fd;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  cursor: pointer;
}
.map-link-btn:hover {
  background: #dbeafe;
}
.query-box {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
}
.query-box-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-basis: 100%;
}
.query-box-title {
  flex: 0 0 auto;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}
.query-box-notice {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-left: auto;
  color: #334155;
  font-size: 13px;
}
.notice-more {
  border: 0;
  background: transparent;
  color: #0c98fe;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
}
.data-quality-detail {
  flex-basis: 100%;
  margin: 0;
  color: #475569;
  font-size: 12px;
  line-height: 1.45;
}
.query-box .query-submit {
  margin-left: auto;
  background: #0c98fe;
  border-color: #0c98fe;
  color: #fff;
}
.query-break {
  flex-basis: 100%;
  height: 0;
}
.data-quality-notice.is-summary {
  border-color: #dbeafe;
  background: #eef6ff;
  color: #334155;
}
.data-quality-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.notice-more {
  border: 0;
  background: transparent;
  color: #0c98fe;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
}
.data-quality-detail {
  margin: 8px 0 0;
}
.supply-tabs.status-view-tabs button {
  border: 0;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  background: transparent;
  color: #64748b;
}
.supply-tabs.status-view-tabs button.active {
  background: transparent;
  border-color: transparent;
  border-bottom-color: #0c98fe;
  color: #0c98fe;
}
.plan-summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 4px 0 12px;
}
.plan-summary-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 14px;
  min-height: 32px;
}
.plan-metric {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  border: 0;
  background: transparent;
  padding: 2px 0;
  color: #64748b;
  font-size: 13px;
}
button.plan-metric {
  cursor: pointer;
}
button.plan-metric.on {
  box-shadow: inset 0 -2px 0 #0c98fe;
}
.plan-metric strong {
  color: #0c98fe;
  font-size: 15px;
  font-weight: 700;
}
.plan-kicker {
  color: #334155;
  font-size: 13px;
  font-weight: 700;
}
.plan-rule {
  width: 1px;
  height: 14px;
  background: #cbd5e1;
}
.plan-empty,
.plan-result-count {
  color: #64748b;
  font-size: 12px;
}
.plan-list-tools {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.plan-search-label {
  color: #334155;
  font-size: 13px;
  font-weight: 700;
}
.plan-table-wrap {
  overflow: auto;
  max-height: calc(100vh - 360px);
}
.plan-style-table {
  border-collapse: separate;
  border-spacing: 0;
}
.plan-style-table th,
.plan-style-table td {
  height: 38px;
  white-space: nowrap;
}
.plan-style-table thead th {
  position: sticky;
  top: 28px;
  background: #f8fafc;
}
.plan-style-table .plan-group-row th {
  top: 0;
  height: 28px;
  text-align: center;
  background: #eef6ff;
}
.plan-style-table td.num,
.plan-style-table th.num {
  text-align: right;
}
.plan-style-table td.muted {
  color: #94a3b8;
}
.plan-paging {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.plan-paging-nav {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
